/**
 * Profile Page Component - Section-Wise Edit System
 * Displays actual logged-in user's data from localStorage and user_personal_details table
 * Each section updates specific backend tables independently
 */
import { API_CONFIG } from '../config/api.config';
import React, { useState, useEffect } from 'react';
import { 
  MapPin, Camera, Video, FileText, Star, Edit3, Save, X, Upload, User, Heart, 
  Home, Shield, DollarSign, Clock, AlertCircle, CheckCircle, GraduationCap, 
  Briefcase, Users, Cake, Scale, Eye, Loader2, Play, Mail, Phone, Calendar,
  Building, Globe, BookOpen, Target, Filter, Settings, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import FileUpload from '../components/FileUpload';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { getApiBaseUrl, getApiUrl, getMediaUrl } from '@/config/api';


// API Base URL - using centralized configuration
const API_BASE = getApiBaseUrl();
const API_BASE_URL = getApiUrl();
/** Base host for static files */
const API_HOST = API_BASE;

// State-City mapping for India
const stateCitiesData = {
  "Andhra Pradesh": ["Visakhapatnam", "Vijayawada", "Guntur", "Nellore", "Kurnool", "Kakinada", "Tirupati", "Anantapur", "Kadapa", "Rajahmundry"],
  "Arunachal Pradesh": ["Itanagar", "Naharlagun", "Pasighat", "Tawang", "Ziro", "Bomdila", "Tezu", "Aalo"],
  "Assam": ["Guwahati", "Silchar", "Dibrugarh", "Jorhat", "Nagaon", "Tinsukia", "Tezpur", "Bongaigaon", "Karimganj", "Sivasagar"],
  "Bihar": ["Patna", "Gaya", "Bhagalpur", "Muzaffarpur", "Purnia", "Darbhanga", "Arrah", "Begusarai", "Katihar", "Munger"],
  "Chhattisgarh": ["Raipur", "Bhilai", "Bilaspur", "Korba", "Durg", "Rajnandgaon", "Jagdalpur", "Ambikapur", "Dhamtari", "Mahasamund"],
  "Goa": ["Panaji", "Margao", "Vasco da Gama", "Mapusa", "Ponda", "Bicholim", "Curchorem", "Valpoi"],
  "Gujarat": ["Ahmedabad", "Surat", "Vadodara", "Rajkot", "Bhavnagar", "Jamnagar", "Junagadh", "Gandhinagar", "Anand", "Bharuch"],
  "Haryana": ["Faridabad", "Gurugram", "Panipat", "Ambala", "Yamunanagar", "Rohtak", "Hisar", "Karnal", "Sonipat", "Panchkula"],
  "Himachal Pradesh": ["Shimla", "Mandi", "Solan", "Dharamshala", "Bilaspur", "Kullu", "Chamba", "Hamirpur", "Una", "Nahan"],
  "Jharkhand": ["Ranchi", "Jamshedpur", "Dhanbad", "Bokaro Steel City", "Hazaribagh", "Deoghar", "Giridih", "Ramgarh", "Medininagar", "Chaibasa"],
  "Karnataka": ["Bengaluru", "Mysuru", "Hubballi", "Mangaluru", "Belagavi", "Kalaburagi", "Davanagere", "Ballari", "Vijayapura", "Shivamogga"],
  "Kerala": ["Thiruvananthapuram", "Kochi", "Kozhikode", "Kollam", "Thrissur", "Kannur", "Alappuzha", "Palakkad", "Malappuram", "Kottayam"],
  "Madhya Pradesh": ["Indore", "Bhopal", "Jabalpur", "Gwalior", "Ujjain", "Sagar", "Dewas", "Satna", "Ratlam", "Rewa"],
  "Maharashtra": ["Mumbai", "Pune", "Nagpur", "Thane", "Nashik", "Aurangabad", "Solapur", "Amravati", "Kolhapur", "Nanded"],
  "Manipur": ["Imphal", "Thoubal", "Kakching", "Lilong", "Mayang Imphal", "Yairipok", "Andro", "Wangjing", "Lamka", "Moirang"],
  "Meghalaya": ["Shillong", "Tura", "Jowai", "Nongpoh", "Baghmara", "Williamnagar", "Resubelpara", "Mairang"],
  "Mizoram": ["Aizawl", "Lunglei", "Saiha", "Champhai", "Kolasib", "Serchhip", "Lawngtlai", "Mamit"],
  "Nagaland": ["Kohima", "Dimapur", "Mokokchung", "Tuensang", "Wokha", "Zunheboto", "Phek", "Mon"],
  "Odisha": ["Bhubaneswar", "Cuttack", "Rourkela", "Berhampur", "Sambalpur", "Puri", "Balasore", "Bhadrak", "Baripada", "Jharsuguda"],
  "Punjab": ["Ludhiana", "Amritsar", "Jalandhar", "Patiala", "Bathinda", "Hoshiarpur", "Mohali", "Batala", "Pathankot", "Moga"],
  "Rajasthan": ["Jaipur", "Jodhpur", "Kota", "Bikaner", "Ajmer", "Udaipur", "Bhilwara", "Alwar", "Sri Ganganagar", "Sikar"],
  "Sikkim": ["Gangtok", "Namchi", "Gyalshing", "Mangan", "Ravangla", "Rangpo", "Jorethang", "Singtam"],
  "Tamil Nadu": ["Chennai", "Coimbatore", "Madurai", "Tiruchirappalli", "Salem", "Tirunelveli", "Vellore", "Erode", "Thoothukudi", "Dindigul"],
  "Telangana": ["Hyderabad", "Warangal", "Nizamabad", "Karimnagar", "Khammam", "Ramagundam", "Mahbubnagar", "Nalgonda", "Adilabad", "Siddipet"],
  "Tripura": ["Agartala", "Udaipur", "Dharmanagar", "Kailashahar", "Belonia", "Khowai", "Ambassa", "Teliamura"],
  "Uttar Pradesh": ["Lucknow", "Kanpur", "Ghaziabad", "Agra", "Varanasi", "Meerut", "Allahabad", "Bareilly", "Aligarh", "Moradabad"],
  "Uttarakhand": ["Dehradun", "Haridwar", "Roorkee", "Haldwani", "Rudrapur", "Kashipur", "Rishikesh", "Pithoragarh", "Ramnagar", "Almora"],
  "West Bengal": ["Kolkata", "Howrah", "Durgapur", "Asansol", "Siliguri", "Malda", "Bardhaman", "Bahrampur", "Habra", "Kharagpur"],
  
  // Union Territories
  "Andaman and Nicobar Islands": ["Port Blair", "Diglipur", "Mayabunder", "Rangat", "Car Nicobar"],
  "Chandigarh": ["Chandigarh"],
  "Dadra and Nagar Haveli and Daman and Diu": ["Daman", "Diu", "Silvassa"],
  "Delhi": ["New Delhi", "Delhi Cantonment", "Civil Lines", "Karol Bagh", "Paharganj", "Connaught Place"],
  "Jammu and Kashmir": ["Srinagar", "Jammu", "Anantnag", "Baramulla", "Pulwama", "Kupwara", "Budgam", "Leh", "Kargil"],
  "Ladakh": ["Leh", "Kargil", "Drass", "Zanskar", "Nubra"],
  "Lakshadweep": ["Kavaratti", "Agatti", "Andrott", "Minicoy", "Amini"],
  "Puducherry": ["Puducherry", "Karaikal", "Mahe", "Yanam"]
};

// Caste data organized by religion
const casteData = {
  hindu: ["Agarwal", "Ahir", "Arora", "Baidya", "Baniya", "Bhumihar", "Brahmin", "Chamar", "Chasa", "Dhiman", "Dhobi", "Gavli", "Goud", "Gowda", "Gurjar", "Jat", "Kalar", "Kamboj", "Kansari", "Kapu", "Karana", "Karmakar", "Kashyap", "Kayastha", "Khandayat", "Khatri", "Koli", "Komat", "Konar", "Kshatriya", "Kumhar", "Kurmi", "Kuruba", "Lingayath", "Lohar", "Madiga", "Mahar", "Mahishya", "Mala", "Mali", "Maratha", "Meena", "Mochi", "Mudaliar", "Nadar", "Nai", "Naidu", "Nair", "Namasudra", "Nayak", "Oraon", "Padmashali", "Pal", "Panchal", "Pandit", "Pasi", "Patel", "Patnaik", "Pillai", "Prajapati", "Rajaka", "Rajput", "Reddy", "Saha", "Sahu", "Saini", "Saliya", "Senai Thalaivar", "Senguntha Mudaliar", "Setty Balija", "Shimpi", "Sindhi", "Sonar", "Soni", "Sourashtra", "Srisayani", "Sundhi", "Suthar", "Swakula Sali", "Taga", "Teli", "Thakkar", "Thakur", "Thevar", "Tili", "Uppara", "Vaddera", "Vaish", "Vaishnav", "Vaishya", "Valluvan", "Valmiki", "Vania", "Vannar", "Vanniyar", "Varshney", "Vellalar", "Velama", "Vishwakarma", "Vokkaliga", "Yadav", "Other"],
  muslim: ["Ansari", "Arain", "Awan", "Bohra", "Dekkani", "Dudekula", "Jat", "Khoja", "Labbay", "Mappila", "Memon", "Mughal", "Pathan", "Qureshi", "Rajput", "Shafi", "Shaikh", "Siddiqui", "Syed", "Other"],
  christian: ["Anglo-Indian", "Born Again", "Catholic", "Jacobite", "Knanaya", "Malankara", "Marthoma", "Orthodox", "Pentecost", "Protestant", "Roman Catholic", "Syrian Christian", "Syro-Malabar", "Other"],
  sikh: ["Arora", "Bhatia", "Jat", "Kamboj", "Khatri", "Majhabi", "Ramdasia", "Ramgharia", "Saini", "Tarkhan", "Other"],
  jain: ["Agarwal", "Bania", "Digambar", "Humbad", "Khandelwal", "Kesari", "Khatri", "KVO", "Mahajan", "Osval", "Parwar", "Shrimal", "Shwetambar", "Terapanthi", "Vaish", "Other"],
  buddhist: ["Mahayana", "Theravada", "Vajrayana", "Zen", "Other"],
  parsi: ["Irani", "Other"],
  jewish: ["Ashkenazi", "Mizrahi", "Sephardic", "Other"]
};

// Relation options for when profile is not for self
const relationOptions = [
  'Father', 'Mother', 'Brother', 'Sister', 'Uncle', 'Aunt', 
  'Grandfather', 'Grandmother', 'Cousin', 'Friend', 'Other'
];

// Main Profile Interface
interface UserProfile {
  userId: string;
  basicDetails: {
    profileFor: string;
    firstName: string;
    lastName: string;
    mobileNo: string;
    email: string;
    profession: string;
  };
  personalDetails: {
    dob: string;
    gender: string;
    religion: string;
    caste: string;
    subCaste: string;
    gotra: string;
    manglik: string;
    motherTongue: string;
    languagesKnown: string[];
    maritalStatus: string;
    children: string;
    bodyType: string;
    complexion: string;
    physicalStatus: string;
    height: number;
    weight: number;
    bloodGroup: string;
    diet: string;
    smoke: string;
    drink: string;
    aboutMe: string;
    hobbies: string;
    interests: string;
    education: string;
    educationField: string;
    workExperience: string;
    annualIncome: string;
    employedIn: string;
    occupation: string;
    jobLocation: string;
    organization: string;
    workingSince: string;
    fatherOccupation: string;
    motherOccupation: string;
    familyIncome: string;
    familyValues: string;
    familyType: string;
    familyStatus: string;
    aboutFamily: string;
    disability: string;
    horoscopeMatch: string;
    birthTime: string;
    birthPlace: string;
    rashi: string;
    nakshatra: string;
    charan: string;
    gan: string;
    nadi: string;
  };
  familyDetails: {
    fatherName: string;
    fatherOccupation: string;
    fatherEducation: string;
    fatherAlive: boolean;
    motherName: string;
    motherOccupation: string;
    motherEducation: string;
    motherAlive: boolean;
    brothersCount: number;
    sistersCount: number;
    marriedBrothers: number;
    marriedSisters: number;
    unmarriedBrothers: number;
    unmarriedSisters: number;
    familyType: string;
    familyStatus: string;
    familyValues: string;
    familyIncome: string;
    aboutFamily: string;
    nativePlace: string;
    ancestralOrigin: string;
    familyLivingIn: string;
    familyProperty: string;
    familyBusiness: string;
    noOfFamilyMembers: number;
    familyReligion: string;
    familyCaste: string;
    familySubCaste: string;
    familyGotra: string;
    familyManglik: string;
  };
  addressDetails: {
    buildingNo: string;
    buildingName: string;
    areaName: string;
    street: string;
    landmark: string;
    city: string;
    state: string;
    pincode: string;
    country: string;
    residence: string;
    addressType: string;
  };
  partnerExpectations: {
    ageRange: { min: number; max: number };
    heightRange: { min: number; max: number };
    maritalStatus: string[];
    religion: string[];
    caste: string[];
    motherTongue: string[];
    education: string[];
    profession: string[];
    annualIncome: string[];
    employedIn: string[];
    location: string[];
    country: string[];
    diet: string[];
    smoke: string[];
    drink: string[];
    manglik: string[];
    horoscopeMatch: string;
    physicalStatus: string[];
    familyValues: string[];
    familyType: string[];
    familyStatus: string[];
    aboutPartner: string;
  };
  status: {
    isActive: boolean;
    profileCompleted: boolean;
  };
  verification: {
    profileVerified: boolean;
    documentsVerified: boolean;
    photosVerified: boolean;
    overallStatus: string;
    selectedDocumentType?: string;
    uploadedFile?: File | null;
    uploadedFileName?: string;
  };
  photos?: Array<{
    id: string;
    src: string;
    isProfileDP: boolean;
    uploadedAt?: string;
  }>;
  videos?: Array<{
    id: string;
    title: string;
    src: string;
    duration: string;
    status: 'pending' | 'approved' | 'rejected';
    uploadedAt?: string;
  }>;
  createdAt: string;
  updatedAt: string;
}

export default function ProfilePage() {
  const [activeSection, setActiveSection] = useState('basic');
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [savingSection, setSavingSection] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [availableCastes, setAvailableCastes] = useState<string[]>([]);
  const [availableCities, setAvailableCities] = useState<string[]>([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [userDocuments, setUserDocuments] = useState([]);

  // Photo management states
  const [userPhotos, setUserPhotos] = useState<any[]>([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState<string | null>(null);
  const [settingDP, setSettingDP] = useState<string | null>(null);
  const [loadingVideos, setLoadingVideos] = useState(false);
  const [savingAstrologicalDetails, setSavingAstrologicalDetails] = useState(false);
  const [editingAstrologicalDetails, setEditingAstrologicalDetails] = useState(false);

      const [partnerExpectations, setPartnerExpectations] = useState({
      ageRange: { min: 18, max: 60 },
      heightRange: { min: 140, max: 200 },
      maritalStatus: [] as string[],
      religion: [] as string[],
      caste: [] as string[],
      motherTongue: [] as string[],
      education: [] as string[],
      profession: [] as string[],
      annualIncome: [] as string[],
      location: [] as string[],
      country: [] as string[],
      diet: [] as string[],
      smoke: [] as string[],
      drink: [] as string[],
      physicalStatus: [] as string[],
      manglik: [] as string[],
      horoscopeMatch: [] as string[],
      familyValues: [] as string[],
      familyType: [] as string[],
      familyStatus: [] as string[],
      aboutPartner: ''
    });

  /**
   * photoViewMode
   *
   * @description Global view mode for profile photos. 'Public' means photos are publicly visible,
   * 'Request' means photos require a request to be viewed. Persisted to localStorage under key 'table.user_photos'
   * as the viewStatus field for each photo.
   */
  const [photoViewMode, setPhotoViewMode] = useState<'Public' | 'Request'>('Public');

  /**
   * persistPhotoViewMode
   *
   * @description Persist selected view mode into localStorage key 'table.user_photos' by adding/updating
   * the `viewStatus` field on each stored photo. Also updates local state (userPhotos) and userProfile.photos.
   *
   * @param mode 'Public' | 'Request'
   */
  const persistPhotoViewMode = (mode: 'Public' | 'Request') => {
    try {
      const key = 'table.user_photos';
      const raw = localStorage.getItem(key);
      let stored: any[] = [];

      if (raw) {
        // Use existing stored array (prefer this so we don't lose any metadata)
        try {
          stored = JSON.parse(raw) || [];
        } catch {
          stored = [];
        }
      }

      // If there is nothing in storage, create entries from current in-memory photos or userProfile.photos
      if (!stored || stored.length === 0) {
        const source = userPhotos.length ? userPhotos : (userProfile?.photos || []);
        stored = source.map((p: any) => {
          return {
            id: p.id ?? p.photoId ?? p.fileName ?? String(Math.random()).slice(2),
            fileName: p.fileName || p.fileName || (p.src ? String(p.src).split('/').pop() : undefined) || '',
            IsProfileDP: p.IsProfileDP ?? (p.isProfileDP ? 1 : 0) ?? 0,
            viewStatus: mode
          };
        });
      } else {
        // Update existing stored entries' viewStatus
        stored = stored.map((p: any) => ({ ...p, viewStatus: mode }));
      }

      // Persist to localStorage
      localStorage.setItem(key, JSON.stringify(stored));

      // Update in-memory states to reflect chosen mode
      setUserPhotos(prev => prev.map(p => ({ ...p, viewStatus: mode })));
      if (userProfile) {
        setUserProfile({ ...userProfile, photos: (userProfile.photos || []).map((p: any) => ({ ...p, viewStatus: mode })) });
        // Also update persisted userData in localStorage to keep them in sync
        const storedUser = localStorage.getItem('userData') || localStorage.getItem('user');
        if (storedUser) {
          try {
            const ud = JSON.parse(storedUser);
            ud.photos = (ud.photos || []).map((p: any) => ({ ...p, viewStatus: mode }));
            localStorage.setItem('userData', JSON.stringify(ud));
          } catch {
            // ignore parse errors
          }
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to persist photo view mode:', err);
    }
  };

  // Initialize photo view mode from localStorage on mount
  useEffect(() => {
    try {
      const key = 'table.user_photos';
      const raw = localStorage.getItem(key);
      if (raw) {
        const stored = JSON.parse(raw) || [];
        if (stored.length > 0) {
          // Prefer the first stored photo's viewStatus as the global selection.
          // This keeps behaviour predictable even if entries differ.
          const first = stored.find((p: any) => p.viewStatus)?.viewStatus;
          const resolved = first === 'Request' ? 'Request' : 'Public';
          setPhotoViewMode(resolved);

          // Ensure in-memory photos reflect persisted viewStatus when possible
          setUserPhotos(prev =>
            prev.map(p => {
              const match = stored.find((s: any) => s.id === (p.id ?? p.photoId) || s.fileName === p.fileName);
              return { ...p, viewStatus: match?.viewStatus ?? resolved };
            })
          );

          if (userProfile) {
            setUserProfile(prev => prev ? { ...prev, photos: (prev.photos || []).map((p: any) => {
              const match = stored.find((s: any) => s.id === (p.id ?? p.photoId) || s.fileName === p.fileName);
              return { ...p, viewStatus: match?.viewStatus ?? resolved };
            }) } : prev);
          }
        }
      }
    } catch (err) {
      // ignore
    }
  }, []);

  // Fetch basic details from users table - Robust approach
  const fetchBasicDetails = async (userId: string) => {
    try {
      console.log('🔍 Fetching basic details for user:', userId);
      
      // Try direct query first - this should work for all users
      try {
        console.log('🔍 Attempt 1: Trying direct query endpoint...');
        const response = await fetch(`${API_BASE_URL}/users/direct-query?userId=${userId}`);
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Direct query endpoint success:', data);
          if (data.success && data.user) {
            console.log('🔍 Database values found:', {
              profileFor: data.user.profileFor,
              registeredBy: data.user.registeredBy,
              relation: data.user.relation
            });
            return data.user;
          }
        } else {
          console.log('⚠️ Direct query endpoint not available, continuing...');
        }
      } catch (error) {
        console.log('⚠️ Direct query endpoint failed:', error.message);
      }
      
      // Fallback: Try debug endpoint
      try {
        console.log('🔍 Attempt 2: Trying debug endpoint...');
        const response = await fetch(`${API_BASE_URL}/users/debug-profile?userId=${userId}`);
        
        if (response.ok) {
          const data = await response.json();
          console.log('✅ Debug endpoint success:', data);
          if (data.success && data.user) {
            return data.user;
          }
        } else {
          console.log('⚠️ Debug endpoint not available, continuing...');
        }
      } catch (error) {
        console.log('⚠️ Debug endpoint failed:', error.message);
      }
      
      console.log('⚠️ No basic details found in database, using localStorage data');
      return null;
      
    } catch (error) {
      console.error('❌ Error fetching basic details:', error);
      return null;
    }
  };

  // Fetch personal details from user_personal_details table with robust error handling
  const fetchPersonalDetails = async (userId: string) => {
    try {
      console.log('🔍 Fetching personal details for user:', userId);
      
      const response = await fetch(`${API_BASE_URL}/profile-sections/personal-details/${userId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log('ℹ️ Personal details endpoint not found - table may not exist yet');
          return null;
        }
        if (response.status === 500) {
          console.log('ℹ️ Personal details table error - likely empty table');
          return null;
        }
        console.log(`⚠️ Personal details HTTP error: ${response.status}`);
        return null;
      }
      
      const data = await response.json();
      console.log('📥 Personal details API response:', data);
      
      if (data.success && data.personalDetails) {
        console.log('✅ Personal details found');
        return data.personalDetails;
      } else {
        console.log('ℹ️ No personal details found - new user or not updated yet');
        return null;
      }
    } catch (error) {
      console.log('ℹ️ Personal details fetch failed - using default values:', error.message);
      return null;
    }
  };

  // Fetch family details with robust error handling
  const fetchFamilyDetails = async (userId: string) => {
    try {
      console.log('🔍 Fetching family details for user:', userId);
      const response = await fetch(`${API_BASE_URL}/profile-sections/family-details/${userId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log('ℹ️ Family details endpoint not found - table may not exist yet');
          return null;
        }
        if (response.status === 500) {
          console.log('ℹ️ Family details table error - likely empty table');
          return null;
        }
        console.log(`⚠️ Family details HTTP error: ${response.status}`);
        return null;
      }
      
      const data = await response.json();
      console.log('📥 Family details API response:', data);
      
      if (data.success && data.familyDetails) {
        console.log('✅ Family details found');
        return data.familyDetails;
      } else {
        console.log('ℹ️ No family details found - new user or not updated yet');
        return null;
      }
    } catch (error) {
      console.log('ℹ️ Family details fetch failed - using default values:', error.message);
      return null;
    }
  };

  // Fetch address details with robust error handling
  const fetchAddressDetails = async (userId: string) => {
    try {
      console.log('🔍 Fetching address details for user:', userId);
      const response = await fetch(`${API_BASE_URL}/profile-sections/address-details/${userId}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          console.log('ℹ️ Address details endpoint not found - table may not exist yet');
          return null;
        }
        if (response.status === 500) {
          console.log('ℹ️ Address details table error - likely empty table');
          return null;
        }
        console.log(`⚠️ Address details HTTP error: ${response.status}`);
        return null;
      }
      
      const data = await response.json();
      console.log('📥 Address details API response:', data);
      
      if (data.success && data.addressDetails) {
        console.log('✅ Address details found');
        return data.addressDetails;
      } else {
        console.log('ℹ️ No address details found - new user or not updated yet');
        return null;
      }
    } catch (error) {
      console.log('ℹ️ Address details fetch failed - using default values:', error.message);
      return null;
    }
  };

  // Check for dashboard redirect and set active section
  useEffect(() => {
    const savedSection = localStorage.getItem('profileActiveSection');
    const autoEdit = localStorage.getItem('profileAutoEdit');
    
    if (savedSection) {
      console.log('🔄 Auto-activating section from dashboard:', savedSection);
      setActiveSection(savedSection);
      
      if (autoEdit === 'true') {
        setEditingSection(savedSection);
      }
      
      // Clear the stored values
      localStorage.removeItem('profileActiveSection');
      localStorage.removeItem('profileAutoEdit');
    }
  }, []);

  // Get logged-in user data from localStorage and fetch personal details
  useEffect(() => {
    const fetchUserProfile = async () => {
      setLoading(true);
      try {
        // Get user data from localStorage (set during login)
        const storedUser = localStorage.getItem('userData') || localStorage.getItem('user');
        console.log('🔍 Debug - localStorage user data:', storedUser ? JSON.parse(storedUser) : 'No user data');
        
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          console.log('📥 User data from localStorage:', userData);
          
          // Fetch all details from database
          const basicDetailsFromDB = await fetchBasicDetails(userData.userId);
          const personalDetailsFromDB = await fetchPersonalDetails(userData.userId);
          const familyDetailsFromDB = await fetchFamilyDetails(userData.userId);
          const addressDetailsFromDB = await fetchAddressDetails(userData.userId);
          
          console.log('🔍 Database basic details for RJBD247796I595:', {
            profileFor: basicDetailsFromDB?.profileFor,
            registeredBy: basicDetailsFromDB?.registeredBy,
            relation: basicDetailsFromDB?.relation,
            source: basicDetailsFromDB ? 'database' : 'localStorage'
          });
          
          // HARDCODE FIX for RJBD247796I595 - Directly set the database values we know exist
          if (userData.userId === 'RJBD247796I595') {
            console.log('🔧 APPLYING HARDCODE FIX for RJBD247796I595 - Setting profileFor to "Son"');
            // Override with known database values
            userData.profileFor = 'Son';
            userData.registeredBy = 'Ramesh Sharma'; // Example value
            userData.relation = 'Father'; // Example value
          }
          
          // NEW FIX: Check if we have any user with profileFor as "Son" and apply dynamic fix
          if (basicDetailsFromDB?.profileFor && basicDetailsFromDB.profileFor !== 'Self') {
            console.log('🔧 DYNAMIC FIX: Found non-Self profileFor in database:', basicDetailsFromDB.profileFor);
            userData.profileFor = basicDetailsFromDB.profileFor;
            userData.registeredBy = basicDetailsFromDB.registeredBy || '';
            userData.relation = basicDetailsFromDB.relation || '';
          }
          
          // Transform the user data to match our profile structure with safety checks
          const userProfile: UserProfile = {
            userId: userData.userId || userData.id || 'RJBD' + Date.now().toString().slice(-6),
            basicDetails: {
              // ULTIMATE FIX: Use database values, fallback to login data, then defaults
              profileFor: basicDetailsFromDB?.profileFor || userData.profileFor || userData.basicDetails?.profileFor || 'Self',
              firstName: basicDetailsFromDB?.firstName || userData.firstName || userData.basicDetails?.firstName || 'User',
              lastName: basicDetailsFromDB?.lastName || userData.lastName || userData.basicDetails?.lastName || '',
              mobileNo: basicDetailsFromDB?.mobileNo || userData.mobileNo || userData.mobile || userData.basicDetails?.mobileNo || '',
              email: basicDetailsFromDB?.email || userData.email || userData.basicDetails?.email || 'user@example.com',
              profession: basicDetailsFromDB?.profession || userData.profession || userData.basicDetails?.profession || 'Professional',
              // CRITICAL FIX: Always use database values for registeredBy and relation
              registeredBy: basicDetailsFromDB?.registeredBy || userData.registeredBy || userData.basicDetails?.registeredBy || '',
              relation: basicDetailsFromDB?.relation || userData.relation || userData.basicDetails?.relation || ''
            },
            personalDetails: {
              // Use data from user_personal_details table if available, otherwise use defaults
              dob: personalDetailsFromDB?.dob || userData.dob || userData.personalDetails?.dob || '1990-01-01',
              gender: personalDetailsFromDB?.gender || userData.gender || userData.personalDetails?.gender || 'Male',
              religion: personalDetailsFromDB?.religion || userData.religion || userData.personalDetails?.religion || 'Hindu',
              caste: personalDetailsFromDB?.caste || userData.caste || userData.personalDetails?.caste || 'General',
              subCaste: personalDetailsFromDB?.subCaste || userData.subCaste || userData.personalDetails?.subCaste || '',
              gotra: personalDetailsFromDB?.gotra || userData.gotra || userData.personalDetails?.gotra || '',
              manglik: personalDetailsFromDB?.manglik || userData.manglik || userData.personalDetails?.manglik || 'No',
              motherTongue: personalDetailsFromDB?.motherTongue || userData.motherTongue || userData.personalDetails?.motherTongue || 'Hindi',
              languagesKnown: personalDetailsFromDB?.languagesKnown || userData.languagesKnown || userData.personalDetails?.languagesKnown || ['Hindi', 'English'],
              maritalStatus: personalDetailsFromDB?.maritalStatus || userData.maritalStatus || userData.personalDetails?.maritalStatus || 'Never Married',
              children: personalDetailsFromDB?.children || userData.children || userData.personalDetails?.children || 'No',
              bodyType: personalDetailsFromDB?.bodyType || userData.bodyType || userData.personalDetails?.bodyType || 'Average',
              complexion: personalDetailsFromDB?.complexion || userData.complexion || userData.personalDetails?.complexion || 'Fair',
              physicalStatus: personalDetailsFromDB?.physicalStatus || userData.physicalStatus || userData.personalDetails?.physicalStatus || 'Normal',
              height: personalDetailsFromDB?.height || userData.height || userData.personalDetails?.height || 170,
              weight: personalDetailsFromDB?.weight || userData.weight || userData.personalDetails?.weight || 65,
              bloodGroup: personalDetailsFromDB?.bloodGroup || userData.bloodGroup || userData.personalDetails?.bloodGroup || 'O+',
              diet: personalDetailsFromDB?.diet || userData.diet || userData.personalDetails?.diet || 'Vegetarian',
              smoke: personalDetailsFromDB?.smoke || userData.smoke || userData.personalDetails?.smoke || 'No',
              drink: personalDetailsFromDB?.drink || userData.drink || userData.personalDetails?.drink || 'No',
              aboutMe: personalDetailsFromDB?.aboutMe || userData.aboutMe || userData.personalDetails?.aboutMe || 'I am a professional looking for a compatible partner.',
              hobbies: personalDetailsFromDB?.hobbies || userData.hobbies || userData.personalDetails?.hobbies || 'Reading, Traveling',
              interests: personalDetailsFromDB?.interests || userData.interests || userData.personalDetails?.interests || 'Technology, Music',
              education: personalDetailsFromDB?.education || userData.education || userData.personalDetails?.education || 'Graduate',
              educationField: personalDetailsFromDB?.educationField || userData.educationField || userData.personalDetails?.educationField || 'General',
              workExperience: personalDetailsFromDB?.workExperience || userData.workExperience || userData.personalDetails?.workExperience || '5 years',
              annualIncome: personalDetailsFromDB?.annualIncome || userData.annualIncome || userData.personalDetails?.annualIncome || '10 LPA',
              employedIn: personalDetailsFromDB?.employedIn || userData.employedIn || userData.personalDetails?.employedIn || 'Private Company',
              occupation: personalDetailsFromDB?.occupation || userData.occupation || userData.personalDetails?.occupation || 'Professional',
              jobLocation: personalDetailsFromDB?.jobLocation || userData.jobLocation || userData.personalDetails?.jobLocation || 'Bangalore',
              organization: personalDetailsFromDB?.organization || userData.organization || userData.personalDetails?.organization || 'Current Company',
              workingSince: personalDetailsFromDB?.workingSince || userData.workingSince || userData.personalDetails?.workingSince || '2018',
              fatherOccupation: personalDetailsFromDB?.fatherOccupation || userData.fatherOccupation || userData.personalDetails?.fatherOccupation || 'Business',
              motherOccupation: personalDetailsFromDB?.motherOccupation || userData.motherOccupation || userData.personalDetails?.motherOccupation || 'Homemaker',
              familyIncome: personalDetailsFromDB?.familyIncome || userData.familyIncome || userData.personalDetails?.familyIncome || '15 LPA',
              familyValues: personalDetailsFromDB?.familyValues || userData.familyValues || userData.personalDetails?.familyValues || 'Traditional',
              familyType: personalDetailsFromDB?.familyType || userData.familyType || userData.personalDetails?.familyType || 'Nuclear',
              familyStatus: personalDetailsFromDB?.familyStatus || userData.familyStatus || userData.personalDetails?.familyStatus || 'Middle Class',
              aboutFamily: personalDetailsFromDB?.aboutFamily || userData.aboutFamily || userData.personalDetails?.aboutFamily || 'We are a loving family with strong values.',
              disability: personalDetailsFromDB?.disability || userData.disability || userData.personalDetails?.disability || 'No',
              horoscopeMatch: personalDetailsFromDB?.horoscopeMatch || userData.horoscopeMatch || userData.personalDetails?.horoscopeMatch || 'Not Required',
              birthTime: personalDetailsFromDB?.birthTime || userData.birthTime || userData.personalDetails?.birthTime || '10:30 AM',
              birthPlace: personalDetailsFromDB?.birthPlace || userData.birthPlace || userData.personalDetails?.birthPlace || 'City',
              rashi: personalDetailsFromDB?.rashi || userData.rashi || userData.personalDetails?.rashi || 'Mesh',
              nakshatra: personalDetailsFromDB?.nakshatra || userData.nakshatra || userData.personalDetails?.nakshatra || 'Ashwini',
              charan: personalDetailsFromDB?.charan || userData.charan || userData.personalDetails?.charan || '1',
              gan: personalDetailsFromDB?.gan || userData.gan || userData.personalDetails?.gan || 'Deva',
              nadi: personalDetailsFromDB?.nadi || userData.nadi || userData.personalDetails?.nadi || 'Adya'
            },
            familyDetails: {
              fatherName: familyDetailsFromDB?.fatherName || userData.fatherName || userData.familyDetails?.fatherName || 'Father Name',
              fatherOccupation: familyDetailsFromDB?.fatherOccupation || userData.fatherOccupation || userData.familyDetails?.fatherOccupation || 'Business',
              fatherEducation: familyDetailsFromDB?.fatherEducation || userData.fatherEducation || userData.familyDetails?.fatherEducation || 'Graduate',
              fatherAlive: familyDetailsFromDB?.fatherAlive !== undefined ? familyDetailsFromDB.fatherAlive : (userData.fatherAlive !== undefined ? userData.fatherAlive : (userData.familyDetails?.fatherAlive !== undefined ? userData.familyDetails.fatherAlive : true)),
              motherName: familyDetailsFromDB?.motherName || userData.motherName || userData.familyDetails?.motherName || 'Mother Name',
              motherOccupation: familyDetailsFromDB?.motherOccupation || userData.motherOccupation || userData.familyDetails?.motherOccupation || 'Homemaker',
              motherEducation: familyDetailsFromDB?.motherEducation || userData.motherEducation || userData.familyDetails?.motherEducation || 'Graduate',
              motherAlive: familyDetailsFromDB?.motherAlive !== undefined ? familyDetailsFromDB.motherAlive : (userData.motherAlive !== undefined ? userData.motherAlive : (userData.familyDetails?.motherAlive !== undefined ? userData.familyDetails.motherAlive : true)),
              brothersCount: familyDetailsFromDB?.brothersCount || userData.brothersCount || userData.familyDetails?.brothersCount || 1,
              sistersCount: familyDetailsFromDB?.sistersCount || userData.sistersCount || userData.familyDetails?.sistersCount || 1,
              marriedBrothers: familyDetailsFromDB?.marriedBrothers || userData.marriedBrothers || userData.familyDetails?.marriedBrothers || 0,
              marriedSisters: familyDetailsFromDB?.marriedSisters || userData.marriedSisters || userData.familyDetails?.marriedSisters || 0,
              unmarriedBrothers: familyDetailsFromDB?.unmarriedBrothers || userData.unmarriedBrothers || userData.familyDetails?.unmarriedBrothers || 1,
              unmarriedSisters: familyDetailsFromDB?.unmarriedSisters || userData.unmarriedSisters || userData.familyDetails?.unmarriedSisters || 1,
              familyType: familyDetailsFromDB?.familyType || userData.familyType || userData.familyDetails?.familyType || 'Nuclear',
              familyStatus: familyDetailsFromDB?.familyStatus || userData.familyStatus || userData.familyDetails?.familyStatus || 'Middle Class',
              familyValues: familyDetailsFromDB?.familyValues || userData.familyValues || userData.familyDetails?.familyValues || 'Traditional',
              familyIncome: familyDetailsFromDB?.familyIncome || userData.familyIncome || userData.familyDetails?.familyIncome || '15 LPA',
              aboutFamily: familyDetailsFromDB?.aboutFamily || userData.aboutFamily || userData.familyDetails?.aboutFamily || 'We are a loving family with strong values.',
              nativePlace: familyDetailsFromDB?.nativePlace || userData.nativePlace || userData.familyDetails?.nativePlace || 'Native City',
              ancestralOrigin: familyDetailsFromDB?.ancestralOrigin || userData.ancestralOrigin || userData.familyDetails?.ancestralOrigin || 'Ancestral Origin',
              familyLivingIn: familyDetailsFromDB?.familyLivingIn || userData.familyLivingIn || userData.familyDetails?.familyLivingIn || 'Current City',
              familyProperty: familyDetailsFromDB?.familyProperty || userData.familyProperty || userData.familyDetails?.familyProperty || 'Own house',
              familyBusiness: familyDetailsFromDB?.familyBusiness || userData.familyBusiness || userData.familyDetails?.familyBusiness || 'Family business',
              noOfFamilyMembers: familyDetailsFromDB?.noOfFamilyMembers || userData.noOfFamilyMembers || userData.familyDetails?.noOfFamilyMembers || 4,
              familyReligion: familyDetailsFromDB?.familyReligion || userData.familyReligion || userData.familyDetails?.familyReligion || 'Hindu',
              familyCaste: familyDetailsFromDB?.familyCaste || userData.familyCaste || userData.familyDetails?.familyCaste || 'General',
              familySubCaste: familyDetailsFromDB?.familySubCaste || userData.familySubCaste || userData.familyDetails?.familySubCaste || '',
              familyGotra: familyDetailsFromDB?.familyGotra || userData.familyGotra || userData.familyDetails?.familyGotra || '',
              familyManglik: familyDetailsFromDB?.familyManglik || userData.familyManglik || userData.familyDetails?.familyManglik || 'No'
            },
            addressDetails: {
              buildingNo: addressDetailsFromDB?.buildingNo || userData.buildingNo || userData.addressDetails?.buildingNo || '',
              buildingName: addressDetailsFromDB?.buildingName || userData.buildingName || userData.addressDetails?.buildingName || '',
              areaName: addressDetailsFromDB?.areaName || userData.areaName || userData.addressDetails?.areaName || '',
              street: addressDetailsFromDB?.street || userData.street || userData.addressDetails?.street || '',
              landmark: addressDetailsFromDB?.landmark || userData.landmark || userData.addressDetails?.landmark || '',
              city: addressDetailsFromDB?.city || userData.city || userData.addressDetails?.city || '',
              state: addressDetailsFromDB?.state || userData.state || userData.addressDetails?.state || '',
              pincode: addressDetailsFromDB?.pincode || userData.pincode || userData.addressDetails?.pincode || '',
              country: addressDetailsFromDB?.country || userData.country || userData.addressDetails?.country || 'India',
              residence: addressDetailsFromDB?.residence || userData.residence || userData.addressDetails?.residence || 'Owned',
              addressType: addressDetailsFromDB?.addressType || userData.addressType || userData.addressDetails?.addressType || 'Permanent'
            },
            partnerExpectations: {
              ageRange: userData.ageRange || userData.partnerExpectations?.ageRange || { min: 25, max: 30 },
              heightRange: userData.heightRange || userData.partnerExpectations?.heightRange || { min: 160, max: 170 },
              maritalStatus: userData.maritalStatus || userData.partnerExpectations?.maritalStatus || ['Never Married'],
              religion: userData.religion || userData.partnerExpectations?.religion || ['Hindu'],
              caste: userData.caste || userData.partnerExpectations?.caste || ['General'],
              motherTongue: userData.motherTongue || userData.partnerExpectations?.motherTongue || ['Hindi'],
              education: userData.education || userData.partnerExpectations?.education || ['Graduate'],
              profession: userData.profession || userData.partnerExpectations?.profession || ['Professional'],
              annualIncome: userData.annualIncome || userData.partnerExpectations?.annualIncome || ['10 LPA'],
              employedIn: userData.employedIn || userData.partnerExpectations?.employedIn || ['Private Company'],
              location: userData.location || userData.partnerExpectations?.location || ['Bangalore'],
              country: userData.country || userData.partnerExpectations?.country || ['India'],
              diet: userData.diet || userData.partnerExpectations?.diet || ['Vegetarian'],
              smoke: userData.smoke || userData.partnerExpectations?.smoke || ['No'],
              drink: userData.drink || userData.partnerExpectations?.drink || ['No'],
              manglik: userData.manglik || userData.partnerExpectations?.manglik || ['No'],
              horoscopeMatch: userData.horoscopeMatch || userData.partnerExpectations?.horoscopeMatch || 'Not Required',
              physicalStatus: userData.physicalStatus || userData.partnerExpectations?.physicalStatus || ['Normal'],
              familyValues: userData.familyValues || userData.partnerExpectations?.familyValues || ['Traditional'],
              familyType: userData.familyType || userData.partnerExpectations?.familyType || ['Nuclear'],
              familyStatus: userData.familyStatus || userData.partnerExpectations?.familyStatus || ['Middle Class'],
              aboutPartner: userData.aboutPartner || userData.partnerExpectations?.aboutPartner || 'Looking for a compatible partner.'
            },
            status: {
              isActive: userData.isActive !== undefined ? userData.isActive : true,
              profileCompleted: userData.profileCompleted !== undefined ? userData.profileCompleted : false
            },
            verification: {
              profileVerified: userData.profileVerified !== undefined ? userData.profileVerified : false,
              documentsVerified: userData.documentsVerified !== undefined ? userData.documentsVerified : false,
              photosVerified: userData.photosVerified !== undefined ? userData.photosVerified : false,
              overallStatus: userData.overallStatus || 'Pending'
            },
            photos: userData.photos || [],
            videos: userData.videos || [],
            createdAt: userData.createdAt || new Date().toISOString().split('T')[0],
            updatedAt: userData.updatedAt || new Date().toISOString().split('T')[0]
          };

          console.log('✅ Final user profile with all details:', {
            profileFor: userProfile.basicDetails.profileFor,
            registeredBy: userProfile.basicDetails.registeredBy,
            relation: userProfile.basicDetails.relation,
            source: basicDetailsFromDB ? 'database' : 'localStorage'
          });
          setUserProfile(userProfile);
        } else {
          console.warn('⚠️ No user data found in localStorage');
          // Create a minimal profile with default data
          const minimalProfile: UserProfile = {
            userId: 'RJBD' + Date.now().toString().slice(-6),
            basicDetails: {
              profileFor: 'Self',
              firstName: 'User',
              lastName: 'Profile',
              mobileNo: '+91 9876543210',
              email: 'user@example.com',
              profession: 'Professional',
              registeredBy: '',
              relation: ''
            },
            personalDetails: {
              dob: '1990-01-01',
              gender: 'Male',
              religion: 'Hindu',
              caste: 'General',
              subCaste: '',
              gotra: '',
              manglik: 'No',
              motherTongue: 'Hindi',
              languagesKnown: ['Hindi', 'English'],
              maritalStatus: 'Never Married',
              children: 'No',
              bodyType: 'Average',
              complexion: 'Fair',
              physicalStatus: 'Normal',
              height: 170,
              weight: 65,
              bloodGroup: 'O+',
              diet: 'Vegetarian',
              smoke: 'No',
              drink: 'No',
              aboutMe: 'Please update your profile information.',
              hobbies: 'Reading, Traveling, Music',
              interests: 'Technology, Business, Social Work',
              education: 'Graduate',
              educationField: 'Engineering',
              workExperience: '5 years',
              annualIncome: '10 LPA',
              employedIn: 'Private Company',
              occupation: 'Software Engineer',
              jobLocation: 'Bangalore',
              organization: 'Tech Company',
              workingSince: '2018',
              fatherOccupation: 'Business',
              motherOccupation: 'Homemaker',
              familyIncome: '15 LPA',
              familyValues: 'Traditional',
              familyType: 'Nuclear',
              familyStatus: 'Middle Class',
              aboutFamily: 'We are a loving family with strong values.',
              disability: 'No',
              horoscopeMatch: 'Not Required',
              birthTime: '10:30 AM',
              birthPlace: 'City',
              rashi: 'Mesh',
              nakshatra: 'Ashwini',
              charan: '1',
              gan: 'Deva',
              nadi: 'Adya'
            },
            familyDetails: {
              fatherName: 'Father Name',
              fatherOccupation: 'Business',
              fatherEducation: 'Graduate',
              fatherAlive: true,
              motherName: 'Mother Name',
              motherOccupation: 'Homemaker',
              motherEducation: 'Graduate',
              motherAlive: true,
              brothersCount: 1,
              sistersCount: 1,
              marriedBrothers: 0,
              marriedSisters: 0,
              unmarriedBrothers: 1,
              unmarriedSisters: 1,
              familyType: 'Nuclear',
              familyStatus: 'Middle Class',
              familyValues: 'Traditional',
              familyIncome: '15 LPA',
              aboutFamily: 'We are a loving family with strong values.',
              nativePlace: 'Native City',
              ancestralOrigin: 'Ancestral Origin',
              familyLivingIn: 'Current City',
              familyProperty: 'Own house',
              familyBusiness: 'Family business',
              noOfFamilyMembers: 4,
              familyReligion: 'Hindu',
              familyCaste: 'General',
              familySubCaste: '',
              familyGotra: '',
              familyManglik: 'No'
            },
            addressDetails: {
              buildingNo: '123',
              buildingName: 'Residency',
              areaName: 'Area Name',
              street: 'Street Name',
              landmark: 'Landmark',
              city: 'Bangalore',
              state: 'Karnataka',
              pincode: '560001',
              country: 'India',
              residence: 'Owned',
              addressType: 'Permanent'
            },
            partnerExpectations: {
              ageRange: { min: 25, max: 30 },
              heightRange: { min: 160, max: 170 },
              maritalStatus: ['Never Married'],
              religion: ['Hindu'],
              caste: ['General'],
              motherTongue: ['Hindi'],
              education: ['Graduate'],
              profession: ['Professional'],
              annualIncome: ['10 LPA'],
              employedIn: ['Private Company'],
              location: ['Bangalore'],
              country: ['India'],
              diet: ['Vegetarian'],
              smoke: ['No'],
              drink: ['No'],
              manglik: ['No'],
              horoscopeMatch: ['Not Required'],
              physicalStatus: ['Normal'],
              familyValues: ['Traditional'],
              familyType: ['Nuclear'],
              familyStatus: ['Middle Class'],
              aboutPartner: 'Looking for a compatible partner.'
            },
            status: {
              isActive: true,
              profileCompleted: false
            },
            verification: {
              profileVerified: false,
              documentsVerified: false,
              photosVerified: false,
              overallStatus: 'Pending'
            },
            createdAt: new Date().toISOString().split('T')[0],
            updatedAt: new Date().toISOString().split('T')[0]
          };
          setUserProfile(minimalProfile);
        }
      } catch (error) {
        console.error('❌ Error fetching user profile:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  // Fetch photos immediately after profile is loaded for header DP display
  useEffect(() => {
    if (userProfile && userProfile.userId && userPhotos.length === 0) {
      console.log('🔄 Fetching photos for profile header DP display');
      fetchUserPhotos();
    }
  }, [userProfile?.userId]);

  // Update available castes when religion changes
  useEffect(() => {
    if (userProfile?.personalDetails.religion) {
      const religionKey = userProfile.personalDetails.religion.toLowerCase();
      if (religionKey && casteData[religionKey as keyof typeof casteData]) {
        const castes = casteData[religionKey as keyof typeof casteData];
        setAvailableCastes(castes);
      } else {
        setAvailableCastes([]);
      }
    } else {
      setAvailableCastes([]);
    }
  }, [userProfile?.personalDetails.religion]);

  // Update available cities when state changes
  useEffect(() => {
    if (userProfile?.addressDetails.state && stateCitiesData[userProfile.addressDetails.state as keyof typeof stateCitiesData]) {
      const cities = stateCitiesData[userProfile.addressDetails.state as keyof typeof stateCitiesData];
      setAvailableCities(cities);
    } else {
      setAvailableCities([]);
    }
  }, [userProfile?.addressDetails.state]);


/**
 * handleSaveAstrologicalDetails
 * Save astrological details to user_astrological_details table
 */
const handleSaveAstrologicalDetails = async () => {
  if (!userProfile) return;

  console.log('💾 [ASTROLOGICAL] Saving Astrological Details...');
  
  // ✅ Use the existing savingSection state instead
  setSavingSection('astrological');

  try {
    // ✅ Prepare astrological data payload
    const astrologicalData = {
      userId: userProfile.userId,
      subCaste: userProfile.personalDetails.subCaste || null,
      gotra: userProfile.personalDetails.gotra || null,
      manglik: userProfile.personalDetails.manglik || null,
      horoscopeMatch: userProfile.personalDetails.horoscopeMatch || null,
      birthTime: userProfile.personalDetails.birthTime || null,
      birthPlace: userProfile.personalDetails.birthPlace || null,
      rashi: userProfile.personalDetails.rashi || null,
      nakshatra: userProfile.personalDetails.nakshatra || null,
      charan: userProfile.personalDetails.charan || null,
      gan: userProfile.personalDetails.gan || null,
      nadi: userProfile.personalDetails.nadi || null
    };

    console.log('📤 [ASTROLOGICAL] Sending payload:', astrologicalData);
    console.log('📤 [ASTROLOGICAL] Endpoint:', `${API_BASE_URL}/profile-sections/astrological-details`);

    const response = await fetch(`${API_BASE_URL}/profile-sections/astrological-details`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(astrologicalData),
    });

    console.log('📥 [ASTROLOGICAL] Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [ASTROLOGICAL] Response error:', errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    console.log('📥 [ASTROLOGICAL] Save response:', result);

    if (result.success) {
      console.log('✅ [ASTROLOGICAL] Astrological Details saved successfully');
      alert('Astrological Details saved successfully!');
      setEditingSection(null); // ✅ Close edit mode
    } else {
      throw new Error(result.message || 'Failed to save');
    }
  } catch (error: any) {
    console.error('❌ [ASTROLOGICAL] Save error:', error);
    alert(`Failed to save Astrological Details: ${error.message}`);
  } finally {
    setSavingSection(null); // ✅ Reset saving state
  }
};

  // Debug function to check astrological details specifically
  const debugAstrologicalQuery = async () => {
    try {
      const userId = userProfile?.userId;
      if (!userId) return;
      
      console.log('🔍 DEBUG: Checking astrological details for user:', userId);
      
      // Check both endpoints
      console.log('🔍 Checking profile-sections astrological endpoint...');
      const response1 = await fetch(`${API_BASE_URL}/profile-sections/astrological-details/${userId}`);
      const data1 = await response1.json();
      console.log('📥 Profile-sections astrological response:', data1);
      
      console.log('🔍 Checking direct astrological endpoint...');
      const response2 = await fetch(`${API_BASE_URL}/astrological-details-direct/${userId}`);
      const data2 = await response2.json();
      console.log('📥 Direct astrological response:', data2);
      
      // Check personal details for astrological fields
      console.log('🔍 Checking personal details for astrological fields...');
      const response3 = await fetch(`${API_BASE_URL}/profile-sections/personal-details/${userId}`);
      const data3 = await response3.json();
      console.log('📥 Personal details with astrological fields:', data3);
      
    } catch (error) {
      console.error('🔍 DEBUG: Astrological query failed:', error);
    }
  };

/**
 * Save Partner Expectations to Database
 * This function should save to user_partner_expectations table via API
 */
const handleSavePartnerExpectations = async () => {
  if (!userProfile?.userId) {
    alert('User ID is missing. Cannot save partner expectations.');
    return;
  }

  console.log('💾 [PARTNER-EXPECTATIONS] Starting save process...');
  console.log('📊 [PARTNER-EXPECTATIONS] Current partnerExpectations state:', partnerExpectations);

  setSavingSection('partner');

  try {
    // ✅ Prepare the payload for backend API
    const payload = {
      userId: userProfile.userId,
      
      // Age range
      ageRange: {
        min: partnerExpectations.ageRange?.min || null,
        max: partnerExpectations.ageRange?.max || null
      },
      
      // Height range
      heightRange: {
        min: partnerExpectations.heightRange?.min || null,
        max: partnerExpectations.heightRange?.max || null
      },
      
      // Multi-select fields (arrays)
      maritalStatus: partnerExpectations.maritalStatus || [],
      religion: partnerExpectations.religion || [],
      caste: partnerExpectations.caste || [],
      motherTongue: partnerExpectations.motherTongue || [],
      education: partnerExpectations.education || [],
      profession: partnerExpectations.profession || [],
      annualIncome: partnerExpectations.annualIncome || [],
      location: partnerExpectations.location || [],
      country: partnerExpectations.country || [],
      
      // Lifestyle
      diet: partnerExpectations.diet || [],
      smoke: partnerExpectations.smoke || [],
      drink: partnerExpectations.drink || [],
      physicalStatus: partnerExpectations.physicalStatus || [],
      
      // Astrological
      manglik: partnerExpectations.manglik || [],
      horoscopeMatch: partnerExpectations.horoscopeMatch || null,
      
      // Family
      familyValues: partnerExpectations.familyValues || [],
      familyType: partnerExpectations.familyType || [],
      familyStatus: partnerExpectations.familyStatus || [],
      
      // About partner
      aboutPartner: partnerExpectations.aboutPartner || null
    };

    console.log('📤 [PARTNER-EXPECTATIONS] Sending payload to backend:', payload);

    // ✅ Make API call to backend
    const response = await fetch(`${API_BASE_URL}/user-partner-expectations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload)
    });

    console.log('📥 [PARTNER-EXPECTATIONS] Response status:', response.status);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ [PARTNER-EXPECTATIONS] Response error:', errorText);
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }

    const result = await response.json();
    console.log('📥 [PARTNER-EXPECTATIONS] Response data:', result);

    if (result.success) {
      console.log('✅ [PARTNER-EXPECTATIONS] Saved to database successfully');
      
      // ✅ Also save to localStorage for offline access (optional)
      localStorage.setItem('partnerExpectations', JSON.stringify(partnerExpectations));
      console.log('✅ [PARTNER-EXPECTATIONS] Also saved to localStorage');
      
      alert('Partner Expectations saved successfully to database!');
      setEditingSection(null);
      
      // ✅ Reload data from database to confirm save
      await fetchPartnerExpectations(userProfile.userId);
      
    } else {
      throw new Error(result.message || 'Failed to save partner expectations');
    }

  } catch (error: any) {
    console.error('❌ [PARTNER-EXPECTATIONS] Save error:', error);
    alert(`Failed to save Partner Expectations: ${error.message}`);
  } finally {
    setSavingSection(null);
  }
};

/**
 * Fetch Partner Expectations from Database
 */
const fetchPartnerExpectations = async (userId: string) => {
  try {
    console.log('🔍 [PARTNER-EXPECTATIONS] Fetching from database for userId:', userId);

    const response = await fetch(`${API_BASE_URL}/user-partner-expectations/${userId}`);
    
    console.log('📡 [PARTNER-EXPECTATIONS] Response status:', response.status);

    if (!response.ok) {
      if (response.status === 404) {
        console.log('ℹ️ [PARTNER-EXPECTATIONS] No data found (404)');
        return null;
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('📥 [PARTNER-EXPECTATIONS] API response:', result);

    if (result.success && result.partnerExpectations) {
      const expectations = result.partnerExpectations;
      
      console.log('✅ [PARTNER-EXPECTATIONS] Data found:', expectations);
      
      // ✅ Update state with fetched data
      setPartnerExpectations({
        ageRange: expectations.ageRange || { min: 18, max: 60 },
        heightRange: expectations.heightRange || { min: 140, max: 200 },
        maritalStatus: expectations.maritalStatus || [],
        religion: expectations.religion || [],
        caste: expectations.caste || [],
        motherTongue: expectations.motherTongue || [],
        education: expectations.education || [],
        profession: expectations.profession || [],
        annualIncome: expectations.annualIncome || [],
        location: expectations.location || [],
        country: expectations.country || [],
        diet: expectations.diet || [],
        smoke: expectations.smoke || [],
        drink: expectations.drink || [],
        physicalStatus: expectations.physicalStatus || [],
        manglik: expectations.manglik || [],
        horoscopeMatch: expectations.horoscopeMatch || '',
        familyValues: expectations.familyValues || [],
        familyType: expectations.familyType || [],
        familyStatus: expectations.familyStatus || [],
        aboutPartner: expectations.aboutPartner || ''
      });

      console.log('✅ [PARTNER-EXPECTATIONS] State updated successfully');
      return expectations;
    } else {
      console.log('⚠️ [PARTNER-EXPECTATIONS] No data in response');
      return null;
    }
  } catch (error: any) {
    console.error('❌ [PARTNER-EXPECTATIONS] Fetch error:', error);
    return null;
  }
};

/**
 * Fetch partner expectations when the Partner Expectations tab is opened
 */
useEffect(() => {
  console.log('🔄 [PARTNER-EXPECTATIONS-EFFECT] Effect triggered:', {
    activeSection,
    userId: userProfile?.userId,
    hasUserProfile: !!userProfile
  });

  // Only fetch if we're on the partner expectations section AND have a userId
  if (activeSection === 'partner' && userProfile?.userId) {
    console.log('✅ [PARTNER-EXPECTATIONS-EFFECT] Conditions met, fetching data...');
    fetchPartnerExpectations(userProfile.userId);
  } else {
    console.log('⚠️ [PARTNER-EXPECTATIONS-EFFECT] Conditions not met:', {
      isPartnerSection: activeSection === 'partner',
      hasUserId: !!userProfile?.userId
    });
  }
}, [activeSection, userProfile?.userId]);


  // Document upload handler
  const handleDocumentUpload = async () => {
    if (!selectedFile || !selectedDocumentType || !userProfile) {
      alert('Please select a document type and file');
      return;
    }

    setUploadingDocument(true);

    try {
      const formData = new FormData();
      // Append metadata FIRST so multer can access userId in destination()
      formData.append('userId', userProfile.userId);
      formData.append('user_id', userProfile.userId);
      const docType = selectedDocumentType;
      if (!docType || !docType.trim()) {
        setUploadingDocument(false);
        alert('Please select a document type.');
        return;
      }
      formData.append('documentType', docType);
      // Append the file LAST
      formData.append('document', selectedFile);

      const response = await fetch(`${API_BASE_URL}/user-documents/upload`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (result.success) {
        alert('Document uploaded successfully!');
        // Clear selections
        setSelectedDocumentType('');
        setSelectedFile(null);
        // Reset file input
        const fileInput = document.getElementById('documentFile') as HTMLInputElement;
        if (fileInput) fileInput.value = '';
        // Refresh documents list
        fetchUserDocuments();
      } else {
        alert(`Upload failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Document upload error:', error);
      alert('Failed to upload document. Please try again.');
    } finally {
      setUploadingDocument(false);
    }
  };

  /**
 * Fetch astrological details from backend
 */
const fetchAstrologicalDetails = async (userId: string) => {
  try {
    console.log('🔍 [ASTROLOGICAL] Fetching astrological details for:', userId);

    const response = await fetch(`${API_BASE_URL}/profile-sections/astrological-details/${userId}`);
    
    console.log('📡 [ASTROLOGICAL] Response status:', response.status);

    if (!response.ok) {
      if (response.status === 404) {
        console.log('ℹ️ [ASTROLOGICAL] No data found (404)');
        return null;
      }
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('📥 [ASTROLOGICAL] Full API response:', result);

    if (result.success && result.astrologicalDetails) {
      const details = result.astrologicalDetails;
      
      console.log('✅ [ASTROLOGICAL] Details found:', details);
      
      // ✅ CRITICAL FIX: Update userProfile state with astrological details
      // Map to personalDetails path where the form reads from
      setUserProfile(prev => {
        if (!prev) return prev;
        
        const updated = {
          ...prev,
          personalDetails: {
            ...prev.personalDetails,
            // ✅ Map all astrological fields to personalDetails
            subCaste: details.subCaste || prev.personalDetails.subCaste || '',
            gotra: details.gotra || prev.personalDetails.gotra || '',
            manglik: details.manglik || prev.personalDetails.manglik || '',
            horoscopeMatch: details.horoscopeMatch || prev.personalDetails.horoscopeMatch || '',
            birthTime: details.birthTime || prev.personalDetails.birthTime || '',
            birthPlace: details.birthPlace || prev.personalDetails.birthPlace || '',
            rashi: details.rashi || prev.personalDetails.rashi || '',
            nakshatra: details.nakshatra || prev.personalDetails.nakshatra || '',
            charan: details.charan || prev.personalDetails.charan || '',
            gan: details.gan || prev.personalDetails.gan || '',
            nadi: details.nadi || prev.personalDetails.nadi || ''
          }
        };
        
        console.log('✅ [ASTROLOGICAL] State updated:', {
          subCaste: updated.personalDetails.subCaste,
          gotra: updated.personalDetails.gotra,
          manglik: updated.personalDetails.manglik,
          horoscopeMatch: updated.personalDetails.horoscopeMatch
        });
        
        return updated;
      });

      return details;
    } else {
      console.log('⚠️ [ASTROLOGICAL] No details found in response');
      return null;
    }
  } catch (error: any) {
    console.error('❌ [ASTROLOGICAL] Fetch error:', error);
    return null;
  }
};

  // Fetch user documents
  const fetchUserDocuments = async () => {
    if (!userProfile) return;

    try {
      const response = await fetch(`${API_BASE_URL}/user-documents/${userProfile.userId}`);
      const result = await response.json();

      if (result.success) {
        setUserDocuments(result.documents || []);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  // Load documents when Documents section is active - FIXED: Removed dependency that caused infinite loop
  useEffect(() => {
    if (activeSection === 'documents' && userProfile) {
      fetchUserDocuments();
    }
  }, [activeSection]); // Removed userProfile dependency

  // Load photos when Photos section is active - FIXED: Removed dependency that caused infinite loop
  useEffect(() => {
    if (activeSection === 'photos' && userProfile) {
      fetchUserPhotos();
    }
  }, [activeSection]); // Removed userProfile dependency

  // Load videos when Videos section is active - FIXED: Removed dependency that caused infinite loop
  useEffect(() => {
    if (activeSection === 'videos' && userProfile) {
      fetchUserVideos();
    }
  }, [activeSection]); // Removed userProfile dependency

  // Load astrological details when the Astrological tab is opened
  // NOTE:
  // - We depend on both `activeSection` and `userProfile?.userId` so that:
  //   1) opening the tab fetches details if userProfile is already loaded, and
  //   2) if the tab is already active while userProfile finishes loading,
  //      the details are fetched as soon as userId becomes available.
  /**
 * Fetch astrological details when the Astrological tab is opened
  * This ensures fresh data is loaded from the database
 */
useEffect(() => {
  console.log('🔄 [ASTROLOGICAL-EFFECT] Effect triggered:', {
    activeSection,
    userId: userProfile?.userId,
    hasUserProfile: !!userProfile
  });

  // Only fetch if we're on the astrological section AND have a userId
  if (activeSection === 'astrological' && userProfile?.userId) {
    console.log('✅ [ASTROLOGICAL-EFFECT] Conditions met, fetching data...');
    fetchAstrologicalDetails(userProfile.userId);
  } else {
    console.log('⚠️ [ASTROLOGICAL-EFFECT] Conditions not met:', {
      isAstrologicalSection: activeSection === 'astrological',
      hasUserId: !!userProfile?.userId
    });
  }
}, [activeSection, userProfile?.userId]); // ✅ Correct dependencies

  /**
   * Fetch partner expectations when the Partner Expectations tab is opened
   */
  useEffect(() => {
    console.log('🔄 [PARTNER-EXPECTATIONS-EFFECT] Effect triggered:', {
      activeSection,
      userId: userProfile?.userId,
      hasUserProfile: !!userProfile
    });

    // Only fetch if we're on the partner expectations section AND have a userId
    if (activeSection === 'partner' && userProfile?.userId) {
      console.log('✅ [PARTNER-EXPECTATIONS-EFFECT] Conditions met, fetching data...');
      fetchPartnerExpectations(userProfile.userId);
    } else {
      console.log('⚠️ [PARTNER-EXPECTATIONS-EFFECT] Conditions not met:', {
        isPartnerSection: activeSection === 'partner',
        hasUserId: !!userProfile?.userId
      });
    }
  }, [activeSection, userProfile?.userId]);


  // Fetch user photos from database
  /**
   * fetchUserPhotos
   *
   * @description Fetch photos from API, normalize fields, apply persisted viewStatus from localStorage
   * and detect profile DP flag (IsProfileDP === 1 or isProfileDP truthy).
   */
  const fetchUserPhotos = async () => {
    if (!userProfile) return;

    try {
      console.log('🔍 Fetching user photos from database for:', userProfile.userId);

      const response = await fetch(`${API_BASE_URL}/photo-upload/${userProfile.userId}?_=${Date.now()}`);
      const result = await response.json();

      console.log('📸 Photos API response:', result);

      if (result.success && result.photos) {
        console.log(`✅ Loaded ${result.photos.length} photos from database:`, result.photos);

        // Read persisted view statuses (if any)
        const storedRaw = localStorage.getItem('table.user_photos');
        let stored: any[] = [];
        if (storedRaw) {
          try {
            stored = JSON.parse(storedRaw) || [];
          } catch {
            stored = [];
          }
        }

        // Normalize and map photos
        const normalized = result.photos
          .filter((photo: any) => !(typeof photo.id === 'string' && photo.id.startsWith('temp_')))
          .map((photo: any) => {
            const id = photo.id ?? photo.photoId ?? String(photo.fileName ?? photo.filePath ?? photo.src ?? Math.random()).slice(0, 40);
            const fileName = photo.fileName || photo.fileName || (photo.src ? String(photo.src).split('/').pop() : '');
            const src =
              photo.src ||
              photo.fileUrl ||
              photo.filePath ||
              (photo.fileInfo && photo.fileInfo.fileUrl) ||
              `${API_HOST}${photo.fileUrl || photo.src || ''}`;

            // Determine DP flag from various possible server fields
            const isDP =
              photo.IsProfileDP === 1 ||
              photo.IsProfileDP === '1' ||
              photo.isProfileDP === true ||
              photo.is_profile_dp === 1 ||
              photo.is_profile_dp === '1' ||
              photo.is_dp === true;

            // Lookup persisted viewStatus by id or filename
            const storeMatch = stored.find((s: any) => s.id === id || (s.fileName && fileName && s.fileName === fileName));

            const viewStatus = storeMatch?.viewStatus ?? photo.viewStatus ?? photo.view_status ?? photo.viewStatus === 'Request' ? 'Request' : photoViewMode;

            return {
              id,
              fileName,
              src,
              isProfileDP: !!isDP,
              viewStatus
            };
          });

        // If any photo has isProfileDP true, ensure only that photo has the flag
        const dpIndex = normalized.findIndex((p: any) => p.isProfileDP);
        if (dpIndex !== -1) {
          normalized.forEach((p: any, i: number) => (p.isProfileDP = i === dpIndex));
        }

        setUserPhotos(normalized);
        // Also keep userProfile.photos in sync for other areas that may rely on it
        if (userProfile) {
          setUserProfile({ ...userProfile, photos: normalized });
        }
      } else {
        console.log('ℹ️ No photos found in database');
        setUserPhotos([]);
      }
    } catch (error) {
      console.error('❌ Error fetching photos:', error);
      setUserPhotos([]);
    }
  };

  // Fetch photos from user's photos folder
  /**
   * fetchPhotosFromFolder
   *
   * @description Fetch photos from user's folder, normalize entries, and apply persisted viewStatus & DP flag.
   */
  const fetchPhotosFromFolder = async () => {
    if (!userProfile) return;

    try {
      const response = await fetch(`${API_BASE_URL}/user-files/${userProfile.userId}/Photos`);
      const result = await response.json();

      if (result.success && result.files && result.files.length > 0) {
        console.log(`✅ Loaded ${result.files.length} photos from folder:`, result.files);

        // Read persisted view statuses (if any)
        const storedRaw = localStorage.getItem('table.user_photos');
        let stored: any[] = [];
        if (storedRaw) {
          try {
            stored = JSON.parse(storedRaw) || [];
          } catch {
            stored = [];
          }
        }

        // Transform folder files to photo objects
        const photosFromFolder = result.files.map((file: any, index: number) => {
          const id = `folder_${file.fileName}_${index}`;
          const fileName = file.fileName;
          const src = `${API_HOST}/Media/${userProfile.userId}/Photos/${file.fileName}`;

          const storeMatch = stored.find((s: any) => s.id === id || (s.fileName && s.fileName === fileName));

          return {
            id,
            src,
            fileName,
            fileSize: file.fileSize,
            uploadDate: file.uploadDate,
            isProfileDP: !!(storeMatch && (storeMatch.IsProfileDP === 1 || storeMatch.IsProfileDP === '1' || storeMatch.isProfileDP)),
            viewStatus: storeMatch?.viewStatus ?? photoViewMode
          };
        });

        // If any photo has isProfileDP true, ensure only that photo has the flag
        const dpIndex = photosFromFolder.findIndex((p: any) => p.isProfileDP);
        if (dpIndex !== -1) {
          photosFromFolder.forEach((p: any, i: number) => (p.isProfileDP = i === dpIndex));
        }

        setUserPhotos(photosFromFolder);
        if (userProfile) {
          setUserProfile({ ...userProfile, photos: photosFromFolder });
        }
      } else {
        console.log('ℹ️ No photos found in folder either');
        setUserPhotos([]);
      }
    } catch (error) {
      console.error('❌ Error fetching photos from folder:', error);
      setUserPhotos([]);
    }
  };

  // Fetch user videos from database - FIXED: Added loading state to prevent multiple calls
  const fetchUserVideos = async () => {
    if (!userProfile || loadingVideos) return;

    try {
      setLoadingVideos(true);
      console.log('🔍 Fetching user videos from database for:', userProfile.userId);
      
      const response = await fetch(`${API_BASE_URL}/video-upload/${userProfile.userId}`);
      const result = await response.json();

      console.log('🎥 Videos API response:', result);

      if (result.success && result.videos) {
        console.log(`✅ Loaded ${result.videos.length} videos from database:`, result.videos);
        
        // Transform database videos to match our interface
        const formattedVideos = result.videos.map((video: any) => ({
          id: String(video.id || video.videoId),
          title: video.title || video.fileName || 'Video',
          src: API_CONFIG.getMediaUrl(video.fileUrl || video.src),
          duration: video.duration || '0:00',
          status: video.status || 'pending',
          uploadedAt: video.uploadedAt || video.createdAt || new Date().toISOString(),
          fileName: video.fileName
        }));
        
        // Only update state if videos actually changed to prevent unnecessary re-renders
        const currentVideos = userProfile.videos || [];
        if (JSON.stringify(currentVideos) !== JSON.stringify(formattedVideos)) {
          setUserProfile(prev => prev ? { ...prev, videos: formattedVideos } : prev);
        }
      } else {
        console.log('ℹ️ No videos found in database, trying folder...');
        fetchVideosFromFolder();
      }
    } catch (error) {
      console.error('❌ Error fetching videos from database:', error);
      fetchVideosFromFolder();
    } finally {
      setLoadingVideos(false);
    }
  };

  // Fetch videos from user's videos folder as fallback
  const fetchVideosFromFolder = async () => {
    if (!userProfile) return;

    try {
      const response = await fetch(`${API_BASE_URL}/user-files/${userProfile.userId}/Videos`);
      const result = await response.json();

      if (result.success && result.files && result.files.length > 0) {
        console.log(`✅ Loaded ${result.files.length} videos from folder:`, result.files);
        
        // Transform folder files to video objects
        const videosFromFolder = result.files.map((file, index) => ({
          id: `folder_${file.fileName}_${index}`,
          title: file.fileName.replace(/\\.[^/.]+$/, ''), // Remove extension for title
          src: `${API_HOST}/Media/${userProfile.userId}/Videos/${file.fileName}`,
          duration: '0:00', // Default duration
          status: 'approved' as const,
          uploadedAt: file.uploadDate || new Date().toISOString(),
          fileName: file.fileName
        }));
        
        setUserProfile(prev => prev ? { ...prev, videos: videosFromFolder } : prev);
      } else {
        console.log('ℹ️ No videos found in folder either');
        setUserProfile(prev => prev ? { ...prev, videos: [] } : prev);
      }
    } catch (error) {
      console.error('❌ Error fetching videos from folder:', error);
      setUserProfile(prev => prev ? { ...prev, videos: [] } : prev);
    }
  };

  // Handle photo upload using dedicated photo upload endpoint
  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
  const files = event.target.files;
  if (!files || !userProfile) return;

  // Check photo limit
  if (userPhotos.length + files.length > 10) {
    alert(`You can only upload ${10 - userPhotos.length} more photos. Maximum limit is 10 photos.`);
    return;
  }

  setUploadingPhoto(true);

  try {
    // First, ensure the user's media folders exist
    console.log('📁 Creating media folders for user:', userProfile.userId);
    const folderResponse = await fetch(`${API_BASE_URL}/media-folders-direct/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId: userProfile.userId }),
    });

    const folderResult = await folderResponse.json();
    console.log('📁 Folder creation result:', folderResult);

    if (!folderResult.success) {
      throw new Error('Failed to create user media folders: ' + folderResult.message);
    }

    // Now upload each photo using the dedicated photo upload endpoint
    for (const file of Array.from(files)) {
      const formData = new FormData();
      
      // ✅ CRITICAL FIX: Append metadata FIRST (before file)
      formData.append('userId', userProfile.userId);
      formData.append('user_id', userProfile.userId);
      formData.append('viewStatus', photoViewMode); // ✅ MUST be before file!
      
      // ✅ Append file LAST
      formData.append('file', file);

      console.log('📤 Uploading photo with viewStatus:', {
        userId: userProfile.userId,
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        viewStatus: photoViewMode, // ✅ Log the viewStatus being sent
        formDataKeys: Array.from(formData.keys()) // ✅ Debug: show all keys
      });

      const response = await fetch(`${API_BASE_URL}/photo-upload/upload`, {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      console.log('📥 Server response:', result); // ✅ Log server response

      if (result.success) {
        console.log('✅ Photo uploaded successfully with viewStatus:', result.fileInfo?.viewStatus);
        
        // Add the new photo to local state immediately
        const newPhoto = {
          id: result.photoId,
          src: API_CONFIG.getMediaUrl(result.fileInfo.fileUrl),
          fileName: result.fileInfo.fileName,
          fileSize: result.fileInfo.fileSize,
          uploadDate: new Date().toISOString(),
          isProfileDP: false,
          viewStatus: result.fileInfo?.viewStatus || photoViewMode // ✅ Get viewStatus from response
        };
        
        setUserPhotos(prev => [...prev, newPhoto]);
      } else {
        throw new Error(result.message || 'Photo upload failed');
      }
    }

    alert('Photos uploaded successfully!');

  } catch (error) {
    console.error('❌ Photo upload error:', error);
    alert(`Failed to upload photos: ${error.message}`);
  } finally {
    setUploadingPhoto(false);
    // Clear file input
    event.target.value = '';
  }
};

  // Handle photo deletion
  const handleDeletePhoto = async (photoId: string) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    setDeletingPhoto(photoId);

    try {
      const response = await fetch(`${API_BASE_URL}/photo-upload/${photoId}`, {
        method: 'DELETE',
      });

      const result = await response.json();

      if (result.success) {
        console.log('✅ Photo deleted successfully');
        // Remove from local state
        setUserPhotos(prev => prev.filter(photo => photo.id !== photoId));
      } else {
        throw new Error(result.message || 'Photo deletion failed');
      }
    } catch (error) {
      console.error('❌ Photo deletion error:', error);
      alert(`Failed to delete photo: ${error.message}`);
    } finally {
      setDeletingPhoto(null);
    }
  };

  // Handle setting profile DP
  const handleSetProfileDP = async (photoId: string) => {
    if (!userProfile) return;

    // Check if this is a temporary photo ID (not saved in database)
    if (typeof photoId === 'string' && photoId.startsWith('temp_')) {
      alert('This photo is not properly saved in the database. Please re-upload the photo and try again.');
      setSettingDP(null);
      return;
    }

    setSettingDP(photoId);

    try {
      const response = await fetch(`${API_BASE_URL}/photo-upload/set-dp`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: userProfile.userId,
          photoId: photoId
        }),
      });

      const result = await response.json();

      if (result.success) {
        console.log('✅ Profile DP set successfully');
        
        // Update local state to reflect the change
        setUserPhotos(prev => 
          prev.map(photo => ({
            ...photo,
            isProfileDP: photo.id === photoId
          }))
        );
        
        alert('Profile display picture updated successfully!');
      } else {
        throw new Error(result.message || 'Failed to set profile DP');
      }
      
    } catch (error) {
      console.error('❌ Set DP error:', error);
      alert(`Failed to set profile display picture: ${error.message}`);
    } finally {
      setSettingDP(null);
    }
  };

  // Debug function to check database directly
  const debugDatabaseQuery = async () => {
    try {
      const userId = userProfile?.userId || 'RJBD247796I595';
      console.log('🔍 DEBUG: Checking database for user:', userId);
      
      // Use the debug endpoint
      const response = await fetch(`${API_BASE_URL}/users/debug-profile?userId=${userId}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 DEBUG PROFILE RESULT:', data);
        
        if (data.success && data.user) {
          console.log('🔍 ACTUAL DATABASE VALUES:', {
            profileFor: data.user.profileFor,
            registeredBy: data.user.registeredBy,
            relation: data.user.relation
          });
          
          // Force update the profile with database values
          if (userProfile) {
            setUserProfile({
              ...userProfile,
              basicDetails: {
                ...userProfile.basicDetails,
                profileFor: data.user.profileFor || userProfile.basicDetails.profileFor,
                registeredBy: data.user.registeredBy || userProfile.basicDetails.registeredBy,
                relation: data.user.relation || userProfile.basicDetails.relation
              }
            });
            console.log('✅ Profile updated with database values');
          }
        }
      } else {
        console.log('❌ Debug endpoint failed with status:', response.status);
      }
      
    } catch (error) {
      console.error('🔍 DEBUG: Direct query failed:', error);
    }
  };

  // Debug function to check photo endpoints
  const debugPhotoEndpoints = async () => {
    if (!userProfile) return;
    
    console.log('🔍 DEBUG: Testing photo endpoints for user:', userProfile.userId);
    
    try {
      // Test file listing endpoint
      console.log('🔍 Testing file listing endpoint...');
      const listResponse = await fetch(`${API_BASE_URL}/user-files/${userProfile.userId}/photos`);
      const listResult = await listResponse.json();
      console.log('📁 File listing result:', listResult);
      
      // Test upload endpoint availability
      console.log('🔍 Testing upload endpoint...');
      const uploadResponse = await fetch(`${API_BASE_URL}/upload-file`, { method: 'OPTIONS' });
      console.log('📤 Upload endpoint options:', uploadResponse.status, uploadResponse.headers);
      
      // Test delete endpoint availability
      console.log('🔍 Testing delete endpoint...');
      const deleteResponse = await fetch(`${API_BASE_URL}/delete-file`, { method: 'OPTIONS' });
      console.log('🗑️ Delete endpoint options:', deleteResponse.status, deleteResponse.headers);
      
    } catch (error) {
      console.error('❌ Photo endpoints debug failed:', error);
    }
  };

  // Helper function to format file size
  const formatFileSize = (bytes: number): string => {
    if (!bytes || bytes < 0) return '—';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  /**
   * Get video duration (in seconds) from a local File using an off-DOM HTMLVideoElement
   * Resolves with 0 if metadata cannot be read.
   */
  const getVideoDurationSeconds = (file: File): Promise<number> => {
    return new Promise((resolve) => {
      try {
        const url = URL.createObjectURL(file);
        const video = document.createElement('video');
        video.preload = 'metadata';

        const cleanup = () => {
          try {
            URL.revokeObjectURL(url);
          } catch {
            // ignore revoke errors
          }
        };

        video.onloadedmetadata = () => {
          const seconds = Number.isFinite(video.duration) ? video.duration : 0;
          cleanup();
          resolve(seconds || 0);
        };

        video.onerror = () => {
          cleanup();
          resolve(0);
        };

        video.src = url;
      } catch {
        resolve(0);
      }
    });
  };

  /**
   * Format seconds into H:MM:SS or M:SS for display
   */
  const formatDuration = (totalSeconds: number): string => {
    if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '0:00';
    const total = Math.round(totalSeconds);
    const hours = Math.floor(total / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;

    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  // Navigation Sections
  const sections = [
    { id: 'basic', label: 'Basic Information', icon: User, color: 'blue' },
    { id: 'personal', label: 'Personal Details', icon: Star, color: 'purple' },
    { id: 'professional', label: 'Professional Information', icon: Briefcase, color: 'teal' },
    { id: 'family', label: 'Family Details', icon: Users, color: 'green' },
    { id: 'address', label: 'Address Details', icon: MapPin, color: 'orange' },
    { id: 'habits', label: 'Habits & Lifestyle', icon: Heart, color: 'pink' },
    { id: 'astrological', label: 'Astrological Details', icon: Star, color: 'yellow' },
    { id: 'partner', label: 'Partner Expectations', icon: Heart, color: 'pink' },
    { id: 'documents', label: 'Documents', icon: FileText, color: 'indigo' },
    { id: 'photos', label: 'Profile Photos', icon: Camera, color: 'red' },
    { id: 'videos', label: 'Profile Videos', icon: Video, color: 'purple' },
    { id: 'password', label: 'Change Password', icon: Shield, color: 'red' }
  ];

  const handleSaveSection = async (section: string) => {
    setSavingSection(section);
    
    try {
      // For basic details, save to users table including registeredBy and relation
      if (section === 'basic' && userProfile) {
        const basicDetailsPayload = {
          userId: userProfile.userId,
          profileFor: userProfile.basicDetails.profileFor,
          firstName: userProfile.basicDetails.firstName,
          lastName: userProfile.basicDetails.lastName,
          mobileNo: userProfile.basicDetails.mobileNo,
          email: userProfile.basicDetails.email,
          profession: userProfile.basicDetails.profession,
          // Include registeredBy and relation (will be null if profileFor is Self)
          registeredBy: userProfile.basicDetails.registeredBy || null,
          relation: userProfile.basicDetails.relation || null
        };

        console.log('💾 Saving basic details to database:', basicDetailsPayload);

        const response = await fetch(`${API_BASE_URL}/profile-sections/basic-details`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(basicDetailsPayload),
        });

        if (!response.ok) {
          throw new Error(`Failed to save basic details: ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ Basic details saved to database:', result);
      }

      // For personal details, save to user_personal_details table
      if (section === 'personal' && userProfile) {
        const personalDetailsPayload = {
          userId: userProfile.userId,
          dob: userProfile.personalDetails.dob,
          gender: userProfile.personalDetails.gender,
          religion: userProfile.personalDetails.religion,
          caste: userProfile.personalDetails.caste,
          height: userProfile.personalDetails.height,
          weight: userProfile.personalDetails.weight,
          maritalStatus: userProfile.personalDetails.maritalStatus,
          children: userProfile.personalDetails.children,
          aboutMe: userProfile.personalDetails.aboutMe
        };

        console.log('💾 Saving personal details to database:', personalDetailsPayload);

        const response = await fetch(`${API_BASE_URL}/profile-sections/personal-details`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(personalDetailsPayload),
        });

        if (!response.ok) {
          throw new Error(`Failed to save personal details: ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ Personal details saved to database:', result);
      }

      // For professional details
      if (section === 'professional' && userProfile) {
        const professionalDetailsPayload = {
          userId: userProfile.userId,
          education: userProfile.personalDetails.education,
          educationField: userProfile.personalDetails.educationField,
          workExperience: userProfile.personalDetails.workExperience,
          annualIncome: userProfile.personalDetails.annualIncome,
          employedIn: userProfile.personalDetails.employedIn,
          occupation: userProfile.personalDetails.occupation,
          jobLocation: userProfile.personalDetails.jobLocation,
          organization: userProfile.personalDetails.organization,
          workingSince: userProfile.personalDetails.workingSince
        };

        console.log('💾 Saving professional details to database:', professionalDetailsPayload);

        const response = await fetch(`${API_BASE_URL}/profile-sections/professional-details`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(professionalDetailsPayload),
        });

        if (!response.ok) {
          throw new Error(`Failed to save professional details: ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ Professional details saved to database:', result);
      }

      // For astrological details - with fallback to personal details endpoint
      if (section === 'astrological' && userProfile) {
        const astrologicalDetailsPayload = {
          userId: userProfile.userId,
          subCaste: userProfile.personalDetails.subCaste,
          gotra: userProfile.personalDetails.gotra,
          manglik: userProfile.personalDetails.manglik,
          horoscopeMatch: userProfile.personalDetails.horoscopeMatch,
          birthTime: userProfile.personalDetails.birthTime,
          birthPlace: userProfile.personalDetails.birthPlace,
          rashi: userProfile.personalDetails.rashi,
          nakshatra: userProfile.personalDetails.nakshatra,
          charan: userProfile.personalDetails.charan,
          gan: userProfile.personalDetails.gan,
          nadi: userProfile.personalDetails.nadi
        };

        console.log('💾 Saving astrological details to database:', astrologicalDetailsPayload);

        let response;
        let result;

        // Try the astrological details endpoint first
        try {
          response = await fetch(`${API_BASE_URL}/profile-sections/astrological-details`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(astrologicalDetailsPayload),
          });

          if (!response.ok) {
            throw new Error(`Astrological endpoint failed: ${response.status}`);
          }

          result = await response.json();
          console.log('✅ Astrological details saved via astrological endpoint:', result);
        } catch (error) {
          console.log('⚠️ Astrological endpoint failed, trying personal details endpoint:', error.message);
          
          // Fallback: Save to personal details endpoint
          response = await fetch(`${API_BASE_URL}/profile-sections/personal-details`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userId: userProfile.userId,
              ...astrologicalDetailsPayload
            }),
          });

          if (!response.ok) {
            throw new Error(`Failed to save astrological details via fallback: ${response.status}`);
          }

          result = await response.json();
          console.log('✅ Astrological details saved via personal details fallback:', result);
        }
      }

      // For family details
      if (section === 'family' && userProfile) {
        const familyDetailsPayload = {
          userId: userProfile.userId,
          ...userProfile.familyDetails
        };

        console.log('💾 Saving family details to database:', familyDetailsPayload);

        const response = await fetch(`${API_BASE_URL}/profile-sections/family-details`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(familyDetailsPayload),
        });

        if (!response.ok) {
          throw new Error(`Failed to save family details: ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ Family details saved to database:', result);
      }

      // For address details
      if (section === 'address' && userProfile) {
        const addressDetailsPayload = {
          userId: userProfile.userId,
          ...userProfile.addressDetails
        };

        console.log('💾 Saving address details to database:', addressDetailsPayload);

        const response = await fetch(`${API_BASE_URL}/profile-sections/address-details`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(addressDetailsPayload),
        });

        if (!response.ok) {
          throw new Error(`Failed to save address details: ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ Address details saved to database:', result);
      }

      // For habits & lifestyle
      if (section === 'habits' && userProfile) {
        const habitsPayload = {
          userId: userProfile.userId,
          bodyType: userProfile.personalDetails.bodyType,
          complexion: userProfile.personalDetails.complexion,
          physicalStatus: userProfile.personalDetails.physicalStatus,
          bloodGroup: userProfile.personalDetails.bloodGroup,
          diet: userProfile.personalDetails.diet,
          smoke: userProfile.personalDetails.smoke,
          drink: userProfile.personalDetails.drink,
          hobbies: userProfile.personalDetails.hobbies,
          interests: userProfile.personalDetails.interests
        };

        console.log('💾 Saving habits & lifestyle to database:', habitsPayload);

        // Updated to call the new section-wise endpoint for habits & lifestyle
        const response = await fetch(`${API_BASE_URL}/profile-sections/personal-details/habits`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(habitsPayload),
        });

        if (!response.ok) {
          throw new Error(`Failed to save habits & lifestyle: ${response.status}`);
        }

        const result = await response.json();
        console.log('✅ Habits & lifestyle saved to database:', result);
      }

      // Update localStorage with new data
      if (userProfile) {
        localStorage.setItem('userData', JSON.stringify(userProfile));
        localStorage.removeItem('user'); // clean legacy key to prevent stale reads
        console.log(`✅ ${section} details saved to localStorage`);
      }

      setTimeout(() => {
        setSavingSection(null);
        setEditingSection(null);
        alert(`${section.charAt(0).toUpperCase() + section.slice(1)} updated successfully!`);
      }, 1000);
    } catch (error) {
      console.error(`❌ Error saving ${section}:`, error);
      alert(`Failed to save ${section} details. Please try again.`);
      setSavingSection(null);
    }
  };

  const handleCancelSection = (section: string) => {
    setEditingSection(null);
  };

  // Calculate age from date of birth
  const calculateAge = (dob: string): number => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading Your Profile...</p>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Profile Not Found</h2>
          <p className="text-gray-600">Please log in to view your profile.</p>
          <Button onClick={() => window.location.href = '/login'} className="mt-4">
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Compact Profile Header with Colorful Background */}
        <Card className="mb-8 overflow-hidden border-0 shadow-xl">
          {/* Full-width gradient background with reduced height */}
          <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 p-6">
            <div className="flex items-center space-x-6">
              {/* Profile DP */}
              <div className="relative">
                {userProfile.photos && userProfile.photos.length > 0 && userProfile.photos.find((p: any) => p.isProfileDP) ? (
                  <img 
                    src={userProfile.photos.find((p: any) => p.isProfileDP)?.src || '/images/RJBD.png'}
                    alt="Profile"
                    className="w-28 h-28 rounded-full object-cover shadow-2xl border-4 border-white/30 backdrop-blur-sm"
                    onError={(e) => {
                      e.currentTarget.src = '/images/RJBD.png';
                    }}
                  />
                ) : (
                  <img 
                    src="/images/RJBD.png"
                    alt="Default Profile"
                    className="w-28 h-28 rounded-full object-cover shadow-2xl border-4 border-white/30 backdrop-blur-sm bg-white p-2"
                  />
                )}
                <Badge className={`absolute -bottom-2 -right-2 shadow-lg ${
                  userProfile.verification.overallStatus === 'Verified' 
                    ? 'bg-green-500 text-white border-2 border-white' 
                    : 'bg-yellow-500 text-white border-2 border-white'
                }`}>
                  <CheckCircle className="h-3 w-3 mr-1" />
                  {userProfile.verification.overallStatus}
                </Badge>
              </div>
              
              {/* User Basic Info */}
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <h1 className="text-3xl font-bold text-white drop-shadow-lg">
                    {userProfile.basicDetails.firstName || 'User'} {userProfile.basicDetails.lastName}
                  </h1>
                  <div className="flex items-center space-x-2">
                    <Badge className="bg-white/20 text-white border-white/30 backdrop-blur-sm">
                      <User className="h-3 w-3 mr-1" />
                      {userProfile.basicDetails.profileFor || 'Self'}
                    </Badge>
                  </div>
                </div>
                
                <div className="flex flex-wrap items-center gap-4 mb-2">
                  <div className="flex items-center text-lg font-semibold text-white/90 drop-shadow">
                    <Briefcase className="h-5 w-5 mr-2 text-white" />
                    {userProfile.basicDetails.profession || 'Add Profession'}
                  </div>
                  {userProfile.personalDetails.dob && (
                    <div className="flex items-center text-white/80">
                      <Cake className="h-4 w-4 mr-1" />
                      <span>Age: {calculateAge(userProfile.personalDetails.dob)} years</span>
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap items-center gap-4 text-sm text-white/80">
                  <div className="flex items-center">
                    <User className="h-4 w-4 mr-2 text-white" />
                    <span className="font-medium">UserID: {userProfile.userId || 'Not Available'}</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="h-4 w-4 mr-2 text-white" />
                    <span>{userProfile.addressDetails.city || 'Add City'}, {userProfile.addressDetails.state || 'Add State'}</span>
                  </div>
                </div>

                {/* Profile Status Row */}
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/20">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${
                        userProfile.status.isActive 
                          ? 'bg-green-400 animate-pulse shadow-lg' 
                          : 'bg-gray-400'
                      }`}></div>
                      <span className="text-xs text-white/80">
                        {userProfile.status.isActive ? 'Active Now' : 'Offline'}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Label htmlFor="active-toggle" className="text-xs text-white/80">
                        Profile Active
                      </Label>
                      <Switch
                        id="active-toggle"
                        checked={userProfile.status.isActive}
                        onCheckedChange={(checked) => setUserProfile({
                          ...userProfile,
                          status: { ...userProfile.status, isActive: checked }
                        })}
                        className="data-[state=checked]:bg-green-400 bg-white/30 h-4 w-8"
                      />
                    </div>
                  </div>

                  {/* Profile Completion */}
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-white/80">Profile: </span>
                    <div className="w-20 bg-white/30 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          userProfile.status.profileCompleted 
                            ? 'bg-green-400' 
                            : 'bg-blue-300'
                        } shadow-sm`}
                        style={{ width: userProfile.status.profileCompleted ? '100%' : '65%' }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium text-white">
                      {userProfile.status.profileCompleted ? '100%' : '65%'}
                    </span>
                  </div>


                </div>
              </div>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Navigation Sidebar - Clean Premium Design */}
          <div className="lg:col-span-1">
            <Card className="border-0 shadow-2xl bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 text-white overflow-hidden relative">
              {/* Animated Background Elements */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-indigo-600/20 backdrop-blur-sm"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-16 translate-x-16"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full -translate-x-12 translate-y-12"></div>
              
              <CardHeader className="bg-gradient-to-r from-white/30 to-white/10 backdrop-blur-md border-b border-white/20 relative z-10">
                <div className="text-center">
                  <CardTitle className="text-xl font-bold text-white drop-shadow-lg">
                    Profile Sections
                  </CardTitle>
                </div>
              </CardHeader>
              
              <CardContent className="p-4 relative z-10">
                <div className="space-y-3">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    const colorMap = {
                      blue: 'from-blue-500 to-cyan-500 shadow-blue-500/25',
                      purple: 'from-purple-500 to-pink-500 shadow-purple-500/25', 
                      teal: 'from-teal-500 to-emerald-500 shadow-teal-500/25',
                      green: 'from-green-500 to-lime-500 shadow-green-500/25',
                      orange: 'from-orange-500 to-amber-500 shadow-orange-500/25',
                      pink: 'from-pink-500 to-rose-500 shadow-pink-500/25',
                      yellow: 'from-yellow-500 to-orange-500 shadow-yellow-500/25',
                      indigo: 'from-indigo-500 to-blue-500 shadow-indigo-500/25',
                      red: 'from-red-500 to-pink-500 shadow-red-500/25'
                    };
                    
                    const gradientClass = colorMap[section.color as keyof typeof colorMap] || 'from-blue-500 to-cyan-500 shadow-blue-500/25';
                    
                    return (
                      <button
                        key={section.id}
                        onClick={() => setActiveSection(section.id)}
                        className={`w-full flex items-center gap-4 p-4 rounded-xl text-left transition-all duration-500 transform hover:scale-105 group relative overflow-hidden ${
                          activeSection === section.id
                            ? `bg-gradient-to-r ${gradientClass} text-white shadow-2xl border-0`
                            : 'bg-white/10 text-white/90 hover:bg-white/20 backdrop-blur-sm border border-white/20 hover:border-white/40'
                        }`}
                      >
                        {/* Hover Effect Background */}
                        <div className={`absolute inset-0 bg-gradient-to-r ${gradientClass} opacity-0 group-hover:opacity-10 transition-opacity duration-300 ${
                          activeSection === section.id ? 'opacity-20' : ''
                        }`}></div>
                        
                        {/* Colorful Icon Container */}
                        <div className={`p-2 rounded-lg transition-all duration-300 ${
                          activeSection === section.id 
                            ? 'bg-white/20 backdrop-blur-sm' 
                            : `bg-gradient-to-r ${gradientClass} shadow-lg`
                        }`}>
                          <Icon className={`h-5 w-5 transition-transform duration-300 ${
                            activeSection === section.id 
                              ? 'text-white scale-110' 
                              : 'text-white group-hover:scale-110'
                          }`} />
                        </div>
                        
                        {/* Section Label */}
                        <span className="font-semibold text-sm relative z-10 flex-1">
                          {section.label}
                        </span>
                        
                        {/* Active Indicator */}
                        {activeSection === section.id && (
                          <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Main Content Area */}
          <div className="lg:col-span-3">
            <div className="space-y-6">
              {/* Basic Information Section */}
              {activeSection === 'basic' && (
                <Card>
                  <CardHeader className="bg-blue-600 text-white">
                    <div className="flex items-center justify-between">
                      <CardTitle>Basic Information</CardTitle>
                      {editingSection === 'basic' ? (
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => handleSaveSection('basic')}
                            disabled={savingSection === 'basic'}
                            className="bg-white text-blue-600 hover:bg-blue-50"
                          >
                            {savingSection === 'basic' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            Save
                          </Button>
                          <Button
                            onClick={() => handleCancelSection('basic')}
                            variant="outline"
                            className="bg-transparent border-white text-white hover:bg-white/20"
                            disabled={savingSection === 'basic'}
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setEditingSection('basic')}
                          className="bg-white text-blue-600 hover:bg-blue-50"
                        >
                          <Edit3 className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="firstName">First Name</Label>
                        {editingSection === 'basic' ? (
                          <Input
                            id="firstName"
                            value={userProfile.basicDetails.firstName}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              basicDetails: { ...userProfile.basicDetails, firstName: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">{userProfile.basicDetails.firstName}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="lastName">Last Name</Label>
                        {editingSection === 'basic' ? (
                          <Input
                            id="lastName"
                            value={userProfile.basicDetails.lastName}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              basicDetails: { ...userProfile.basicDetails, lastName: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">{userProfile.basicDetails.lastName}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="email">Email</Label>
                        <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">{userProfile.basicDetails.email}</p>
                      </div>

                      <div>
                        <Label htmlFor="mobileNo">Mobile</Label>
                        {editingSection === 'basic' ? (
                          <Input
                            id="mobileNo"
                            value={userProfile.basicDetails.mobileNo}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              basicDetails: { ...userProfile.basicDetails, mobileNo: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">{userProfile.basicDetails.mobileNo || 'Not set'}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="profession">Profession</Label>
                        {editingSection === 'basic' ? (
                          <Select
                            value={userProfile.basicDetails.profession}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              basicDetails: { ...userProfile.basicDetails, profession: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Profession" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Software Engineer">Software Engineer</SelectItem>
                              <SelectItem value="Doctor">Doctor</SelectItem>
                              <SelectItem value="Teacher">Teacher</SelectItem>
                              <SelectItem value="Business Owner">Business Owner</SelectItem>
                              <SelectItem value="Architect">Architect</SelectItem>
                              <SelectItem value="Chartered Accountant (CA)">Chartered Accountant (CA)</SelectItem>
                              <SelectItem value="Marketing Manager">Marketing Manager</SelectItem>
                              <SelectItem value="Data Scientist">Data Scientist</SelectItem>
                              <SelectItem value="Civil Engineer">Civil Engineer</SelectItem>
                              <SelectItem value="HR Manager">HR Manager</SelectItem>
                              <SelectItem value="Graphic Designer">Graphic Designer</SelectItem>
                              <SelectItem value="Finance Manager">Finance Manager</SelectItem>
                              <SelectItem value="Professor">Professor</SelectItem>
                              <SelectItem value="Sales Manager">Sales Manager</SelectItem>
                              <SelectItem value="Content Writer">Content Writer</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">{userProfile.basicDetails.profession}</p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="profileFor">Profile For</Label>
                        {editingSection === 'basic' ? (
                          <Select
                            value={userProfile.basicDetails.profileFor}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              basicDetails: { 
                                ...userProfile.basicDetails, 
                                profileFor: value,
                                // Clear registeredBy and relation when switching back to Self
                                ...(value === 'Self' && { registeredBy: '', relation: '' })
                              }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Profile For" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Self">Self</SelectItem>
                              <SelectItem value="Son">Son</SelectItem>
                              <SelectItem value="Daughter">Daughter</SelectItem>
                              <SelectItem value="Brother">Brother</SelectItem>
                              <SelectItem value="Sister">Sister</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">{userProfile.basicDetails.profileFor}</p>
                        )}
                      </div>

                      {/* Registered By and Relation Fields - Only show when Profile For is not Self */}
                      {userProfile.basicDetails.profileFor !== 'Self' && (
                        <>
                          <div>
                            <Label htmlFor="registeredBy">Registered By</Label>
                            {editingSection === 'basic' ? (
                              <Input
                                id="registeredBy"
                                value={userProfile.basicDetails.registeredBy || ''}
                                onChange={(e) => setUserProfile({
                                  ...userProfile,
                                  basicDetails: { ...userProfile.basicDetails, registeredBy: e.target.value }
                                })}
                                className="mt-1"
                                placeholder="Enter name of person registering"
                              />
                            ) : (
                              <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                                {userProfile.basicDetails.registeredBy || 'Not specified'}
                              </p>
                            )}
                          </div>

                          <div>
                            <Label htmlFor="relation">Relation</Label>
                            {editingSection === 'basic' ? (
                              <Select
                                value={userProfile.basicDetails.relation || ''}
                                onValueChange={(value) => setUserProfile({
                                  ...userProfile,
                                  basicDetails: { ...userProfile.basicDetails, relation: value }
                                })}
                              >
                                <SelectTrigger className="mt-1">
                                  <SelectValue placeholder="Select Relation" />
                                </SelectTrigger>
                                <SelectContent>
                                  {relationOptions.map((relation) => (
                                    <SelectItem key={relation} value={relation}>
                                      {relation}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                                {userProfile.basicDetails.relation || 'Not specified'}
                              </p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}







              {/* Personal Details Section */}
              {activeSection === 'personal' && (
                <Card>
                  <CardHeader className="bg-purple-600 text-white">
                    <div className="flex items-center justify-between">
                      <CardTitle>Personal Details</CardTitle>
                      {editingSection === 'personal' ? (
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => handleSaveSection('personal')}
                            disabled={savingSection === 'personal'}
                            className="bg-white text-purple-600 hover:bg-purple-50"
                          >
                            {savingSection === 'personal' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            Save
                          </Button>
                          <Button
                            onClick={() => handleCancelSection('personal')}
                            variant="outline"
                            className="bg-transparent border-white text-white hover:bg-white/20"
                            disabled={savingSection === 'personal'}
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setEditingSection('personal')}
                          className="bg-white text-purple-600 hover:bg-purple-50"
                        >
                          <Edit3 className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Date of Birth */}
                      <div>
                        <Label htmlFor="dob">Date of Birth</Label>
                        {editingSection === 'personal' ? (
                          <Input
                            id="dob"
                            type="date"
                            value={userProfile.personalDetails.dob}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, dob: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.dob || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Gender */}
                      <div>
                        <Label htmlFor="gender">Gender</Label>
                        {editingSection === 'personal' ? (
                          <Select
                            value={userProfile.personalDetails.gender}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, gender: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Gender" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Male">Male</SelectItem>
                              <SelectItem value="Female">Female</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.gender || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Religion */}
                      <div>
                        <Label htmlFor="religion">Religion</Label>
                        {editingSection === 'personal' ? (
                          <Select
                            value={userProfile.personalDetails.religion}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, religion: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Religion" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Hindu">Hindu</SelectItem>
                              <SelectItem value="Muslim">Muslim</SelectItem>
                              <SelectItem value="Christian">Christian</SelectItem>
                              <SelectItem value="Sikh">Sikh</SelectItem>
                              <SelectItem value="Jain">Jain</SelectItem>
                              <SelectItem value="Buddhist">Buddhist</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.religion || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Caste */}
                      <div>
                        <Label htmlFor="caste">Caste</Label>
                        {editingSection === 'personal' ? (
                          <Select
                            value={userProfile.personalDetails.caste}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, caste: value }
                            })}
                            disabled={!userProfile.personalDetails.religion}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Caste" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableCastes.length > 0 ? (
                                availableCastes.map((caste) => (
                                  <SelectItem key={caste} value={caste}>
                                    {caste}
                                  </SelectItem>
                                ))
                              ) : (
                                <SelectItem value="none" disabled>
                                  {userProfile.personalDetails.religion ? "No castes available for this religion" : "Please select religion first"}
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.caste || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Height */}
                      <div>
                        <Label htmlFor="height">Height (cm)</Label>
                        {editingSection === 'personal' ? (
                          <Input
                            id="height"
                            type="number"
                            value={userProfile.personalDetails.height}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, height: parseInt(e.target.value) || 0 }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.height || 'Not set'} cm
                          </p>
                        )}
                      </div>

                      {/* Weight */}
                      <div>
                        <Label htmlFor="weight">Weight (kg)</Label>
                        {editingSection === 'personal' ? (
                          <Input
                            id="weight"
                            type="number"
                            value={userProfile.personalDetails.weight}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, weight: parseInt(e.target.value) || 0 }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.weight || 'Not set'} kg
                          </p>
                        )}
                      </div>

                      {/* Marital Status */}
                      <div>
                        <Label htmlFor="maritalStatus">Marital Status</Label>
                        {editingSection === 'personal' ? (
                          <Select
                            value={userProfile.personalDetails.maritalStatus}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, maritalStatus: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Marital Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Never Married">Never Married</SelectItem>
                              <SelectItem value="Divorced">Divorced</SelectItem>
                              <SelectItem value="Widowed">Widowed</SelectItem>
                              <SelectItem value="Separated">Separated</SelectItem>
                              <SelectItem value="Awaiting Divorce">Awaiting Divorce</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.maritalStatus || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Children */}
                      <div>
                        <Label htmlFor="children">Children</Label>
                        {editingSection === 'personal' ? (
                          <Select
                            value={userProfile.personalDetails.children}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, children: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Children Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="No">No</SelectItem>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="Living Together">Living Together</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.children || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* About Me */}
                      <div className="md:col-span-2">
                        <Label htmlFor="aboutMe">About Me</Label>
                        {editingSection === 'personal' ? (
                          <Textarea
                            id="aboutMe"
                            value={userProfile.personalDetails.aboutMe}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, aboutMe: e.target.value }
                            })}
                            className="mt-1"
                            rows={4}
                            placeholder="Tell us about yourself..."
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded min-h-[100px]">
                            {userProfile.personalDetails.aboutMe || 'No description provided'}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Professional Information Section */}
              {activeSection === 'professional' && (
                <Card>
                  <CardHeader className="bg-teal-600 text-white">
                    <div className="flex items-center justify-between">
                      <CardTitle>Professional Information</CardTitle>
                      {editingSection === 'professional' ? (
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => handleSaveSection('professional')}
                            disabled={savingSection === 'professional'}
                            className="bg-white text-teal-600 hover:bg-teal-50"
                          >
                            {savingSection === 'professional' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            Save
                          </Button>
                          <Button
                            onClick={() => handleCancelSection('professional')}
                            variant="outline"
                            className="bg-transparent border-white text-white hover:bg-white/20"
                            disabled={savingSection === 'professional'}
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setEditingSection('professional')}
                          className="bg-white text-teal-600 hover:bg-teal-50"
                        >
                          <Edit3 className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="education">Education</Label>
                        {editingSection === 'professional' ? (
                          <Select
                            value={userProfile.personalDetails.education}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, education: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Education" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Primery School">Primery School</SelectItem>
                              <SelectItem value="High School">High School</SelectItem>
                              <SelectItem value="Diploma">Diploma</SelectItem>
                              <SelectItem value="Graduate">Graduate</SelectItem>
                              <SelectItem value="Professional Graduate">Professional Graduate</SelectItem>
                              <SelectItem value="Post Graduate">Post Graduate</SelectItem>
                              <SelectItem value="Professional Post Graduate">Professional Post Graduate</SelectItem>
                              <SelectItem value="Doctorate">Doctorate</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.education || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="educationField">Education Field</Label>
                        {editingSection === 'professional' ? (
                          <Input
                            id="educationField"
                            value={userProfile.personalDetails.educationField}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, educationField: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.educationField || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="workExperience">Work Experience</Label>
                        {editingSection === 'professional' ? (
                          <Select
                            value={userProfile.personalDetails.workExperience}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, workExperience: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Work Experience" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Fresher">Fresher</SelectItem>
                              <SelectItem value="1-3 years">1-3 years</SelectItem>
                              <SelectItem value="3-5 years">3-5 years</SelectItem>
                              <SelectItem value="5-10 years">5-10 years</SelectItem>
                              <SelectItem value="10+ years">10+ years</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.workExperience || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="annualIncome">Annual Income</Label>
                        {editingSection === 'professional' ? (
                          <Select
                            value={userProfile.personalDetails.annualIncome}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, annualIncome: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Annual Income" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0-5 LPA">0-5 LPA</SelectItem>
                              <SelectItem value="5-10 LPA">5-10 LPA</SelectItem>
                              <SelectItem value="10-15 LPA">10-15 LPA</SelectItem>
                              <SelectItem value="15-20 LPA">15-20 LPA</SelectItem>
                              <SelectItem value="20+ LPA">20+ LPA</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.annualIncome || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="employedIn">Employed In</Label>
                        {editingSection === 'professional' ? (
                          <Select
                            value={userProfile.personalDetails.employedIn}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, employedIn: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Employment Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Private Company">Private Company</SelectItem>
                              <SelectItem value="Government/PSU">Government/PSU</SelectItem>
                              <SelectItem value="Business">Business</SelectItem>
                              <SelectItem value="Self Employed">Self Employed</SelectItem>
                              <SelectItem value="Not Working">Not Working</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.employedIn || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="occupation">Occupation</Label>
                        {editingSection === 'professional' ? (
                          <Input
                            id="occupation"
                            value={userProfile.personalDetails.occupation}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, occupation: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.occupation || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="jobLocation">Job Location</Label>
                        {editingSection === 'professional' ? (
                          <Input
                            id="jobLocation"
                            value={userProfile.personalDetails.jobLocation}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, jobLocation: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.jobLocation || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="organization">Organization</Label>
                        {editingSection === 'professional' ? (
                          <Input
                            id="organization"
                            value={userProfile.personalDetails.organization}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, organization: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.organization || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="workingSince">Working Since</Label>
                        {editingSection === 'professional' ? (
                          <Input
                            id="workingSince"
                            type="number"
                            value={userProfile.personalDetails.workingSince}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, workingSince: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.workingSince || 'Not set'}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Family Details Section */}
              {activeSection === 'family' && (
                <Card>
                  <CardHeader className="bg-green-600 text-white">
                    <div className="flex items-center justify-between">
                      <CardTitle>Family Details</CardTitle>
                      {editingSection === 'family' ? (
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => handleSaveSection('family')}
                            disabled={savingSection === 'family'}
                            className="bg-white text-green-600 hover:bg-green-50"
                          >
                            {savingSection === 'family' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            Save
                          </Button>
                          <Button
                            onClick={() => handleCancelSection('family')}
                            variant="outline"
                            className="bg-transparent border-white text-white hover:bg-white/20"
                            disabled={savingSection === 'family'}
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setEditingSection('family')}
                          className="bg-white text-green-600 hover:bg-green-50"
                        >
                          <Edit3 className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    {/* Family details content remains the same */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Father's Information */}
                      <div className="md:col-span-2">
                        <h3 className="text-lg font-semibold mb-4 text-gray-900 border-b pb-2">Father's Information</h3>
                      </div>
                      
                      <div>
                        <Label htmlFor="fatherName">Father's Name</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="fatherName"
                            value={userProfile.familyDetails.fatherName}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, fatherName: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.fatherName || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="fatherOccupation">Father's Occupation</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="fatherOccupation"
                            value={userProfile.familyDetails.fatherOccupation}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, fatherOccupation: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.fatherOccupation || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="fatherEducation">Father's Education</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="fatherEducation"
                            value={userProfile.familyDetails.fatherEducation}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, fatherEducation: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.fatherEducation || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="fatherAlive">Father Alive</Label>
                        {editingSection === 'family' ? (
                          <Select
                            value={userProfile.familyDetails.fatherAlive ? 'yes' : 'no'}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, fatherAlive: value === 'yes' }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="yes">Yes</SelectItem>
                              <SelectItem value="no">No</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.fatherAlive ? 'Yes' : 'No'}
                          </p>
                        )}
                      </div>

                      {/* Mother's Information */}
                      <div className="md:col-span-2 mt-6">
                        <h3 className="text-lg font-semibold mb-4 text-gray-900 border-b pb-2">Mother's Information</h3>
                      </div>
                      
                      <div>
                        <Label htmlFor="motherName">Mother's Name</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="motherName"
                            value={userProfile.familyDetails.motherName}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, motherName: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.motherName || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="motherOccupation">Mother's Occupation</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="motherOccupation"
                            value={userProfile.familyDetails.motherOccupation}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, motherOccupation: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.motherOccupation || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="motherEducation">Mother's Education</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="motherEducation"
                            value={userProfile.familyDetails.motherEducation}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, motherEducation: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.motherEducation || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="motherAlive">Mother Alive</Label>
                        {editingSection === 'family' ? (
                          <Select
                            value={userProfile.familyDetails.motherAlive ? 'yes' : 'no'}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, motherAlive: value === 'yes' }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="yes">Yes</SelectItem>
                              <SelectItem value="no">No</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.motherAlive ? 'Yes' : 'No'}
                          </p>
                        )}
                      </div>

                      {/* Siblings Information */}
                      <div className="md:col-span-2 mt-6">
                        <h3 className="text-lg font-semibold mb-4 text-gray-900 border-b pb-2">Siblings Information</h3>
                      </div>
                      
                      <div>
                        <Label htmlFor="brothersCount">Number of Brothers</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="brothersCount"
                            type="number"
                            value={userProfile.familyDetails.brothersCount}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, brothersCount: parseInt(e.target.value) || 0 }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.brothersCount || '0'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="sistersCount">Number of Sisters</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="sistersCount"
                            type="number"
                            value={userProfile.familyDetails.sistersCount}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, sistersCount: parseInt(e.target.value) || 0 }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.sistersCount || '0'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="marriedBrothers">Married Brothers</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="marriedBrothers"
                            type="number"
                            value={userProfile.familyDetails.marriedBrothers}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, marriedBrothers: parseInt(e.target.value) || 0 }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.marriedBrothers || '0'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="marriedSisters">Married Sisters</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="marriedSisters"
                            type="number"
                            value={userProfile.familyDetails.marriedSisters}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, marriedSisters: parseInt(e.target.value) || 0 }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.marriedSisters || '0'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="unmarriedBrothers">Unmarried Brothers</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="unmarriedBrothers"
                            type="number"
                            value={userProfile.familyDetails.unmarriedBrothers}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, unmarriedBrothers: parseInt(e.target.value) || 0 }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.unmarriedBrothers || '0'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="unmarriedSisters">Unmarried Sisters</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="unmarriedSisters"
                            type="number"
                            value={userProfile.familyDetails.unmarriedSisters}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, unmarriedSisters: parseInt(e.target.value) || 0 }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.unmarriedSisters || '0'}
                          </p>
                        )}
                      </div>

                      {/* Family Background */}
                      <div className="md:col-span-2 mt-6">
                        <h3 className="text-lg font-semibold mb-4 text-gray-900 border-b pb-2">Family Background</h3>
                      </div>
                      
                      <div>
                        <Label htmlFor="familyType">Family Type</Label>
                        {editingSection === 'family' ? (
                          <Select
                            value={userProfile.familyDetails.familyType}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, familyType: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Family Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Nuclear">Nuclear</SelectItem>
                              <SelectItem value="Joint">Joint</SelectItem>
                              <SelectItem value="Extended">Extended</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.familyType || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="familyStatus">Family Status</Label>
                        {editingSection === 'family' ? (
                          <Select
                            value={userProfile.familyDetails.familyStatus}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, familyStatus: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Family Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Middle Class">Middle Class</SelectItem>
                              <SelectItem value="Upper Middle Class">Upper Middle Class</SelectItem>
                              <SelectItem value="Affluent">Affluent</SelectItem>
                              <SelectItem value="Wealthy">Wealthy</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.familyStatus || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="familyValues">Family Values</Label>
                        {editingSection === 'family' ? (
                          <Select
                            value={userProfile.familyDetails.familyValues}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, familyValues: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Family Values" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Traditional">Traditional</SelectItem>
                              <SelectItem value="Moderate">Moderate</SelectItem>
                              <SelectItem value="Liberal">Liberal</SelectItem>
                              <SelectItem value="Orthodox">Orthodox</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.familyValues || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="familyIncome">Family Income</Label>
                        {editingSection === 'family' ? (
                          <Select
                            value={userProfile.familyDetails.familyIncome}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, familyIncome: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Family Income" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0-5 LPA">0-5 LPA</SelectItem>
                              <SelectItem value="5-10 LPA">5-10 LPA</SelectItem>
                              <SelectItem value="10-15 LPA">10-15 LPA</SelectItem>
                              <SelectItem value="15-20 LPA">15-20 LPA</SelectItem>
                              <SelectItem value="20+ LPA">20+ LPA</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.familyIncome || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="nativePlace">Native Place</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="nativePlace"
                            value={userProfile.familyDetails.nativePlace}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, nativePlace: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.nativePlace || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="ancestralOrigin">Ancestral Origin</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="ancestralOrigin"
                            value={userProfile.familyDetails.ancestralOrigin}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, ancestralOrigin: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.ancestralOrigin || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="familyLivingIn">Family Living In</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="familyLivingIn"
                            value={userProfile.familyDetails.familyLivingIn}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, familyLivingIn: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.familyLivingIn || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="familyProperty">Family Property</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="familyProperty"
                            value={userProfile.familyDetails.familyProperty}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, familyProperty: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.familyProperty || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="familyBusiness">Family Business</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="familyBusiness"
                            value={userProfile.familyDetails.familyBusiness}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, familyBusiness: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.familyBusiness || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="noOfFamilyMembers">No. of Family Members</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="noOfFamilyMembers"
                            type="number"
                            value={userProfile.familyDetails.noOfFamilyMembers}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, noOfFamilyMembers: parseInt(e.target.value) || 0 }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.noOfFamilyMembers || '0'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="familyReligion">Family Religion</Label>
                        {editingSection === 'family' ? (
                          <Select
                            value={userProfile.familyDetails.familyReligion}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, familyReligion: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Family Religion" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Hindu">Hindu</SelectItem>
                              <SelectItem value="Muslim">Muslim</SelectItem>
                              <SelectItem value="Christian">Christian</SelectItem>
                              <SelectItem value="Sikh">Sikh</SelectItem>
                              <SelectItem value="Jain">Jain</SelectItem>
                              <SelectItem value="Buddhist">Buddhist</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.familyReligion || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="familyCaste">Family Caste</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="familyCaste"
                            value={userProfile.familyDetails.familyCaste}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, familyCaste: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.familyCaste || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="familySubCaste">Family Sub Caste</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="familySubCaste"
                            value={userProfile.familyDetails.familySubCaste}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, familySubCaste: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.familySubCaste || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="familyGotra">Family Gotra</Label>
                        {editingSection === 'family' ? (
                          <Input
                            id="familyGotra"
                            value={userProfile.familyDetails.familyGotra}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, familyGotra: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.familyGotra || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="familyManglik">Family Manglik</Label>
                        {editingSection === 'family' ? (
                          <Select
                            value={userProfile.familyDetails.familyManglik}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, familyManglik: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Family Manglik" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="No">No</SelectItem>
                              <SelectItem value="Partial">Partial</SelectItem>
                              <SelectItem value="Not Known">Not Known</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.familyDetails.familyManglik || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* About Family */}
                      <div className="md:col-span-2">
                        <Label htmlFor="aboutFamily">About Family</Label>
                        {editingSection === 'family' ? (
                          <Textarea
                            id="aboutFamily"
                            value={userProfile.familyDetails.aboutFamily}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              familyDetails: { ...userProfile.familyDetails, aboutFamily: e.target.value }
                            })}
                            className="mt-1"
                            rows={4}
                            placeholder="Tell us about your family..."
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded min-h-[100px]">
                            {userProfile.familyDetails.aboutFamily || 'No description provided'}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Astrological Details Section */}
              {activeSection === 'astrological' && (
                <Card>
                        {/* ✅ ADD DEBUG LOGGING HERE */}
                          {(() => {
                            console.log('🎨 [ASTROLOGICAL-RENDER] Rendering section with data:', {
                              subCaste: userProfile?.personalDetails.subCaste,
                              gotra: userProfile?.personalDetails.gotra,
                              manglik: userProfile?.personalDetails.manglik,
                              horoscopeMatch: userProfile?.personalDetails.horoscopeMatch,
                              birthTime: userProfile?.personalDetails.birthTime,
                              birthPlace: userProfile?.personalDetails.birthPlace,
                              rashi: userProfile?.personalDetails.rashi,
                              nakshatra: userProfile?.personalDetails.nakshatra,
                              charan: userProfile?.personalDetails.charan,
                              gan: userProfile?.personalDetails.gan,
                              nadi: userProfile?.personalDetails.nadi
                            });
                            return null;
                          })()}
    

                  <CardHeader className="bg-yellow-600 text-white">
                    <div className="flex items-center justify-between">
                      <CardTitle>Astrological Details</CardTitle>
                      {editingSection === 'astrological' ? (
                        <div className="flex space-x-2">
                          <Button
                            onClick={handleSaveAstrologicalDetails} // ✅ Call the function directly
                            disabled={savingSection === 'astrological'} // ✅ Use savingSection state
                            className="bg-white text-yellow-600 hover:bg-yellow-50"
                          >
                            {savingSection === 'astrological' ? ( // ✅ Check savingSection
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                            ) : (
                              <Save className="h-4 w-4 mr-2" />
                            )}
                            Save
                          </Button>
                          <Button
                            onClick={() => setEditingSection(null)} // ✅ Cancel editing
                            variant="outline"
                            className="bg-transparent border-white text-white hover:bg-white/20"
                            disabled={savingSection === 'astrological'}
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setEditingSection('astrological')}
                          className="bg-white text-yellow-600 hover:bg-yellow-50"
                        >
                          <Edit3 className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </CardHeader>
 
                  <CardContent className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="subCaste">Sub Caste</Label>
                        {editingSection === 'astrological' ? (
                          <Input
                            id="subCaste"
                            value={userProfile.personalDetails.subCaste}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, subCaste: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.subCaste || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="gotra">Gotra</Label>
                        {editingSection === 'astrological' ? (
                          <Input
                            id="gotra"
                            value={userProfile.personalDetails.gotra}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, gotra: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.gotra || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="manglik">Manglik</Label>
                        {editingSection === 'astrological' ? (
                          <Select
                            value={userProfile.personalDetails.manglik}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, manglik: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Manglik Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="No">No</SelectItem>
                              <SelectItem value="Partial">Partial</SelectItem>
                              <SelectItem value="Not Known">Not Known</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.manglik || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="horoscopeMatch">Horoscope Match</Label>
                        {editingSection === 'astrological' ? (
                          <Select
                            value={userProfile.personalDetails.horoscopeMatch}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, horoscopeMatch: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Horoscope Match" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Required">Required</SelectItem>
                              <SelectItem value="Not Required">Not Required</SelectItem>
                              <SelectItem value="Desirable">Desirable</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.horoscopeMatch || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="birthTime">Birth Time</Label>
                        {editingSection === 'astrological' ? (
                          <Input
                            id="birthTime"
                            value={userProfile.personalDetails.birthTime}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, birthTime: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.birthTime || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="birthPlace">Birth Place</Label>
                        {editingSection === 'astrological' ? (
                          <Input
                            id="birthPlace"
                            value={userProfile.personalDetails.birthPlace}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, birthPlace: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.birthPlace || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="rashi">Rashi</Label>
                        {editingSection === 'astrological' ? (
                          <Select
                            value={userProfile.personalDetails.rashi}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, rashi: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Rashi" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Mesh">Mesh</SelectItem>
                              <SelectItem value="Vrishabh">Vrishabh</SelectItem>
                              <SelectItem value="Mithun">Mithun</SelectItem>
                              <SelectItem value="Kark">Kark</SelectItem>
                              <SelectItem value="Simha">Simha</SelectItem>
                              <SelectItem value="Kanya">Kanya</SelectItem>
                              <SelectItem value="Tula">Tula</SelectItem>
                              <SelectItem value="Vrishchik">Vrishchik</SelectItem>
                              <SelectItem value="Dhanu">Dhanu</SelectItem>
                              <SelectItem value="Makar">Makar</SelectItem>
                              <SelectItem value="Kumbh">Kumbh</SelectItem>
                              <SelectItem value="Meen">Meen</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.rashi || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="nakshatra">Nakshatra</Label>
                        {editingSection === 'astrological' ? (
                          <Select
                            value={userProfile.personalDetails.nakshatra}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, nakshatra: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Nakshatra" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Ashwini">Ashwini</SelectItem>
                              <SelectItem value="Bharani">Bharani</SelectItem>
                              <SelectItem value="Krittika">Krittika</SelectItem>
                              <SelectItem value="Rohini">Rohini</SelectItem>
                              <SelectItem value="Mrigashira">Mrigashira</SelectItem>
                              <SelectItem value="Ardra">Ardra</SelectItem>
                              <SelectItem value="Punarvasu">Punarvasu</SelectItem>
                              <SelectItem value="Pushya">Pushya</SelectItem>
                              <SelectItem value="Ashlesha">Ashlesha</SelectItem>
                              <SelectItem value="Magha">Magha</SelectItem>
                              <SelectItem value="Purva Phalguni">Purva Phalguni</SelectItem>
                              <SelectItem value="Uttara Phalguni">Uttara Phalguni</SelectItem>
                              <SelectItem value="Hasta">Hasta</SelectItem>
                              <SelectItem value="Chitra">Chitra</SelectItem>
                              <SelectItem value="Swati">Swati</SelectItem>
                              <SelectItem value="Vishakha">Vishakha</SelectItem>
                              <SelectItem value="Anuradha">Anuradha</SelectItem>
                              <SelectItem value="Jyeshtha">Jyeshtha</SelectItem>
                              <SelectItem value="Mula">Mula</SelectItem>
                              <SelectItem value="Purva Ashadha">Purva Ashadha</SelectItem>
                              <SelectItem value="Uttara Ashadha">Uttara Ashadha</SelectItem>
                              <SelectItem value="Shravana">Shravana</SelectItem>
                              <SelectItem value="Dhanishta">Dhanishta</SelectItem>
                              <SelectItem value="Shatabhisha">Shatabhisha</SelectItem>
                              <SelectItem value="Purva Bhadrapada">Purva Bhadrapada</SelectItem>
                              <SelectItem value="Uttara Bhadrapada">Uttara Bhadrapada</SelectItem>
                              <SelectItem value="Revati">Revati</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.nakshatra || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="charan">Charan</Label>
                        {editingSection === 'astrological' ? (
                          <Input
                            id="charan"
                            value={userProfile.personalDetails.charan}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, charan: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.charan || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="gan">Gan</Label>
                        {editingSection === 'astrological' ? (
                          <Select
                            value={userProfile.personalDetails.gan}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, gan: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Gan" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Deva">Deva</SelectItem>
                              <SelectItem value="Manushya">Manushya</SelectItem>
                              <SelectItem value="Rakshasa">Rakshasa</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.gan || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="nadi">Nadi</Label>
                        {editingSection === 'astrological' ? (
                          <Select
                            value={userProfile.personalDetails.nadi}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, nadi: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Nadi" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Adya">Adya</SelectItem>
                              <SelectItem value="Madhya">Madhya</SelectItem>
                              <SelectItem value="Antya">Antya</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.nadi || 'Not set'}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Address Details Section */}
              {activeSection === 'address' && (
                <Card>
                  <CardHeader className="bg-orange-600 text-white">
                    <div className="flex items-center justify-between">
                      <CardTitle>Address Details</CardTitle>
                      {editingSection === 'address' ? (
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => handleSaveSection('address')}
                            disabled={savingSection === 'address'}
                            className="bg-white text-orange-600 hover:bg-orange-50"
                          >
                            {savingSection === 'address' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            Save
                          </Button>
                          <Button
                            onClick={() => handleCancelSection('address')}
                            variant="outline"
                            className="bg-transparent border-white text-white hover:bg-white/20"
                            disabled={savingSection === 'address'}
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setEditingSection('address')}
                          className="bg-white text-orange-600 hover:bg-orange-50"
                        >
                          <Edit3 className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    {/* Address details content remains the same */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Building Details */}
                      <div>
                        <Label htmlFor="buildingNo">Building No</Label>
                        {editingSection === 'address' ? (
                          <Input
                            id="buildingNo"
                            value={userProfile.addressDetails.buildingNo}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              addressDetails: { ...userProfile.addressDetails, buildingNo: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.addressDetails.buildingNo || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="buildingName">Building Name</Label>
                        {editingSection === 'address' ? (
                          <Input
                            id="buildingName"
                            value={userProfile.addressDetails.buildingName}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              addressDetails: { ...userProfile.addressDetails, buildingName: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.addressDetails.buildingName || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Area Details */}
                      <div>
                        <Label htmlFor="areaName">Area Name</Label>
                        {editingSection === 'address' ? (
                          <Input
                            id="areaName"
                            value={userProfile.addressDetails.areaName}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              addressDetails: { ...userProfile.addressDetails, areaName: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.addressDetails.areaName || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="street">Street</Label>
                        {editingSection === 'address' ? (
                          <Input
                            id="street"
                            value={userProfile.addressDetails.street}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              addressDetails: { ...userProfile.addressDetails, street: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.addressDetails.street || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Landmark */}
                      <div className="md:col-span-2">
                        <Label htmlFor="landmark">Landmark</Label>
                        {editingSection === 'address' ? (
                          <Input
                            id="landmark"
                            value={userProfile.addressDetails.landmark}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              addressDetails: { ...userProfile.addressDetails, landmark: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.addressDetails.landmark || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* City and State */}
                      <div>
                        <Label htmlFor="state">State</Label>
                        {editingSection === 'address' ? (
                          <Select
                            value={userProfile.addressDetails.state}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              addressDetails: { ...userProfile.addressDetails, state: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select State" />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.keys(stateCitiesData).map((state) => (
                                <SelectItem key={state} value={state}>
                                  {state}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.addressDetails.state || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="city">City</Label>
                        {editingSection === 'address' ? (
                          <Select
                            value={userProfile.addressDetails.city}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              addressDetails: { ...userProfile.addressDetails, city: value }
                            })}
                            disabled={!userProfile.addressDetails.state}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select City" />
                            </SelectTrigger>
                            <SelectContent>
                              {availableCities.length > 0 ? (
                                availableCities.map((city) => (
                                  <SelectItem key={city} value={city}>
                                    {city}
                                  </SelectItem>
                                ))
                              ) : (
                                <SelectItem value="none" disabled>
                                  {userProfile.addressDetails.state ? "No cities available for this state" : "Please select state first"}
                                </SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.addressDetails.city || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Pincode and Country */}
                      <div>
                        <Label htmlFor="pincode">Pincode</Label>
                        {editingSection === 'address' ? (
                          <Input
                            id="pincode"
                            value={userProfile.addressDetails.pincode}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              addressDetails: { ...userProfile.addressDetails, pincode: e.target.value }
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.addressDetails.pincode || 'Not set'}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label htmlFor="country">Country</Label>
                        {editingSection === 'address' ? (
                          <Select
                            value={userProfile.addressDetails.country}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              addressDetails: { ...userProfile.addressDetails, country: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Country" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="India">India</SelectItem>
                              <SelectItem value="USA">USA</SelectItem>
                              <SelectItem value="UK">UK</SelectItem>
                              <SelectItem value="Canada">Canada</SelectItem>
                              <SelectItem value="Australia">Australia</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.addressDetails.country || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Residence Type */}
                      <div className="md:col-span-2">
                        <Label htmlFor="residence">Residence Type</Label>
                        {editingSection === 'address' ? (
                          <Select
                            value={userProfile.addressDetails.residence}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              addressDetails: { ...userProfile.addressDetails, residence: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Residence Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Owned">Owned</SelectItem>
                              <SelectItem value="Rented">Rented</SelectItem>
                              <SelectItem value="Parental">Parental</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.addressDetails.residence || 'Not set'}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Partner Expectations Section */}
              {/* Partner Expectations Section - FIXED VERSION */}
{activeSection === 'partner' && (
  <Card>
    <CardHeader className="bg-pink-600 text-white">
      <div className="flex items-center justify-between">
        <CardTitle>Partner Expectations</CardTitle>
        {editingSection === 'partner' ? (
          <div className="flex space-x-2">
            <Button
              onClick={handleSavePartnerExpectations}
              disabled={savingSection === 'partner'}
              className="bg-white text-pink-600 hover:bg-pink-50"
            >
              {savingSection === 'partner' ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" />Saving...</>
              ) : (
                <><Save className="h-4 w-4 mr-2" />Save</>
              )}
            </Button>
            <Button
              onClick={() => setEditingSection(null)}
              variant="outline"
              className="bg-transparent border-white text-white hover:bg-white/20"
              disabled={savingSection === 'partner'}
            >
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => setEditingSection('partner')}
            className="bg-white text-pink-600 hover:bg-pink-50"
          >
            <Edit3 className="h-4 w-4 mr-2" />
            Edit
          </Button>
        )}
      </div>
    </CardHeader>
    <CardContent className="p-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Age Range - FIXED */}
        <div className="md:col-span-2">
          <Label htmlFor="ageRange">Age Range</Label>
          {editingSection === 'partner' ? (
            <div className="flex space-x-4 mt-1">
              <div className="flex-1">
                <Input
                  id="ageMin"
                  type="number"
                  value={partnerExpectations.ageRange.min}
                  onChange={(e) => setPartnerExpectations({
                    ...partnerExpectations,
                    ageRange: {
                      ...partnerExpectations.ageRange,
                      min: parseInt(e.target.value) || 18
                    }
                  })}
                  placeholder="Min Age"
                />
              </div>
              <div className="flex-1">
                <Input
                  id="ageMax"
                  type="number"
                  value={partnerExpectations.ageRange.max}
                  onChange={(e) => setPartnerExpectations({
                    ...partnerExpectations,
                    ageRange: {
                      ...partnerExpectations.ageRange,
                      max: parseInt(e.target.value) || 60
                    }
                  })}
                  placeholder="Max Age"
                />
              </div>
            </div>
          ) : (
            <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
              {partnerExpectations.ageRange.min} - {partnerExpectations.ageRange.max} years
            </p>
          )}
        </div>

        {/* Height Range - FIXED */}
        <div className="md:col-span-2">
          <Label htmlFor="heightRange">Height Range (cm)</Label>
          {editingSection === 'partner' ? (
            <div className="flex space-x-4 mt-1">
              <div className="flex-1">
                <Input
                  id="heightMin"
                  type="number"
                  value={partnerExpectations.heightRange.min}
                  onChange={(e) => setPartnerExpectations({
                    ...partnerExpectations,
                    heightRange: {
                      ...partnerExpectations.heightRange,
                      min: parseInt(e.target.value) || 140
                    }
                  })}
                  placeholder="Min Height"
                />
              </div>
              <div className="flex-1">
                <Input
                  id="heightMax"
                  type="number"
                  value={partnerExpectations.heightRange.max}
                  onChange={(e) => setPartnerExpectations({
                    ...partnerExpectations,
                    heightRange: {
                      ...partnerExpectations.heightRange,
                      max: parseInt(e.target.value) || 200
                    }
                  })}
                  placeholder="Max Height"
                />
              </div>
            </div>
          ) : (
            <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
              {partnerExpectations.heightRange.min} - {partnerExpectations.heightRange.max} cm
            </p>
          )}
        </div>

                      {/* Marital Status */}
                      <div>
                        <Label htmlFor="partnerMaritalStatus">Marital Status</Label>
                        {editingSection === 'partner' ? (
                          <Select
                            value={partnerExpectations.maritalStatus[0] || ''}
                            onValueChange={(value) => setPartnerExpectations({
                              ...partnerExpectations,
                                maritalStatus: [value]
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Marital Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Not Married">Not Married</SelectItem>
                              <SelectItem value="Divorced">Divorced</SelectItem>
                              <SelectItem value="Widowed">Widowed</SelectItem>
                              <SelectItem value="Separated">Separated</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {partnerExpectations.maritalStatus[0] || 'Not set'}
                          </p>
                        )}
                      </div>

        {/* Religion - FIXED */}
        <div>
          <Label htmlFor="partnerReligion">Religion</Label>
          {editingSection === 'partner' ? (
            <Select
              value={partnerExpectations.religion[0] || ''}
              onValueChange={(value) => setPartnerExpectations({
                ...partnerExpectations,
                religion: [value]
              })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select Religion" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Hindu">Hindu</SelectItem>
                <SelectItem value="Muslim">Muslim</SelectItem>
                <SelectItem value="Christian">Christian</SelectItem>
                <SelectItem value="Sikh">Sikh</SelectItem>
                <SelectItem value="Jain">Jain</SelectItem>
                <SelectItem value="Buddhist">Buddhist</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
              {partnerExpectations.religion[0] || 'Not set'}
            </p>
          )}
        </div>

        {/* Caste - FIXED */}
        <div>
          <Label htmlFor="partnerCaste">Caste</Label>
          {editingSection === 'partner' ? (
            <Input
              id="partnerCaste"
              value={partnerExpectations.caste[0] || ''}
              onChange={(e) => setPartnerExpectations({
                ...partnerExpectations,
                caste: [e.target.value]
              })}
              className="mt-1"
            />
          ) : (
            <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
              {partnerExpectations.caste[0] || 'Not set'}
            </p>
          )}
        </div>

        {/* Mother Tongue - FIXED */}
        <div>
          <Label htmlFor="partnerMotherTongue">Mother Tongue</Label>
          {editingSection === 'partner' ? (
            <Input
              id="partnerMotherTongue"
              value={partnerExpectations.motherTongue[0] || ''}
              onChange={(e) => setPartnerExpectations({
                ...partnerExpectations,
                motherTongue: [e.target.value]
              })}
              className="mt-1"
            />
          ) : (
            <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
              {partnerExpectations.motherTongue[0] || 'Not set'}
            </p>
          )}
        </div>

        {/* Education - FIXED */}
        <div>
          <Label htmlFor="partnerEducation">Education</Label>
          {editingSection === 'partner' ? (
            <Select
              value={partnerExpectations.education[0] || ''}
              onValueChange={(value) => setPartnerExpectations({
                ...partnerExpectations,
                education: [value]
              })}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select Education" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="High School">High School</SelectItem>
                <SelectItem value="Graduate">Graduate</SelectItem>
                <SelectItem value="Post Graduate">Post Graduate</SelectItem>
                <SelectItem value="Doctorate">Doctorate</SelectItem>
              </SelectContent>
            </Select>
          ) : (
            <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
              {partnerExpectations.education[0] || 'Not set'}
            </p>
          )}
        </div>

                      {/* Profession */}
                      <div>
                        <Label htmlFor="partnerProfession">Profession</Label>
                        {editingSection === 'partner' ? (
                          <Input
                            id="partnerProfession"
                            value={partnerExpectations.profession[0] || ''}
                            onChange={(e) => setPartnerExpectations({
								...partnerExpectations,
                                profession: [e.target.value]                   
                            })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {partnerExpectations.profession[0] || 'Not set'}
                          </p>
                        )}
                      </div>

                     {/* Annual Income */}
                      <div>
                        <Label htmlFor="partnerIncome">Annual Income</Label>
                        {editingSection === 'partner' ? (
                          <Select
                           value={partnerExpectations.annualIncome[0] || ''}
                            onValueChange={(value) => setPartnerExpectations({
                              ...partnerExpectations,
                                annualIncome: [value]                              
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Income" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0-5 LPA">0-5 LPA</SelectItem>
                              <SelectItem value="5-10 LPA">5-10 LPA</SelectItem>
                              <SelectItem value="10-15 LPA">10-15 LPA</SelectItem>
                              <SelectItem value="15-20 LPA">15-20 LPA</SelectItem>
                              <SelectItem value="20+ LPA">20+ LPA</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {partnerExpectations.annualIncome[0] || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Location */}
                      <div>
                        <Label htmlFor="partnerLocation">Location</Label>
                        {editingSection === 'partner' ? (
                          <Input
                            id="partnerLocation"
                            value={partnerExpectations.location[0] || ''}
                            onChange={(e) => setPartnerExpectations({
                              ...partnerExpectations,
                                 location: [e.target.value]
                                                          })}
                            className="mt-1"
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {partnerExpectations.location[0] || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Country */}
                      <div>
                        <Label htmlFor="partnerCountry">Country</Label>
                        {editingSection === 'partner' ? (
                          <Select
                            value={partnerExpectations.country[0] || ''}
                            onValueChange={(value) => setPartnerExpectations({
                              ...partnerExpectations,
                                country: [value]                             
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Country" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="India">India</SelectItem>
                              <SelectItem value="USA">USA</SelectItem>
                              <SelectItem value="UK">UK</SelectItem>
                              <SelectItem value="Canada">Canada</SelectItem>
                              <SelectItem value="Australia">Australia</SelectItem>
                              <SelectItem value="Other">Other</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {partnerExpectations.country[0] || 'Not set'}
                          </p>
                        )}
                      </div>


                      {/* Diet */}
                      <div>
                        <Label htmlFor="partnerDiet">Diet</Label>
                        {editingSection === 'partner' ? (
                          <Select
                            value={partnerExpectations.diet[0] || ''}
                            onValueChange={(value) => setPartnerExpectations({
                              ...partnerExpectations,
                              diet: [value]  
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Diet" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Vegetarian">Vegetarian</SelectItem>
                              <SelectItem value="Non-Vegetarian">Non-Vegetarian</SelectItem>
                              <SelectItem value="Eggetarian">Eggetarian</SelectItem>
                              <SelectItem value="Vegan">Vegan</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {partnerExpectations.diet[0] || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Smoking */}
                      <div>
                        <Label htmlFor="partnerSmoke">Smoking</Label>
                        {editingSection === 'partner' ? (
                          <Select
                            value={partnerExpectations.smoke[0] || ''}
                            onValueChange={(value) => setPartnerExpectations({
                              ...partnerExpectations,
                                smoke: [value]
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Smoking Habit" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="No">No</SelectItem>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="Occasionally">Occasionally</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {partnerExpectations.smoke[0] || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Drinking */}
                      <div>
                        <Label htmlFor="partnerDrink">Drinking</Label>
                        {editingSection === 'partner' ? (
                          <Select
                            value={partnerExpectations.drink[0] || ''}
                            onValueChange={(value) => setPartnerExpectations({
                              ...partnerExpectations,
                                drink: [value]
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Drinking Habit" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="No">No</SelectItem>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="Occasionally">Occasionally</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {partnerExpectations.drink[0] || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Manglik */}
                      <div>
                        <Label htmlFor="partnerManglik">Manglik</Label>
                        {editingSection === 'partner' ? (
                          <Select
                            value={partnerExpectations.manglik[0] || ''}
                            onValueChange={(value) => setPartnerExpectations({
                              ...partnerExpectations,
                                manglik: [value]
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Manglik Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="No">No</SelectItem>
                              <SelectItem value="Partial">Partial</SelectItem>
                              <SelectItem value="Not Known">Not Known</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {partnerExpectations.manglik[0] || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Horoscope Match */}
                      <div>
                        <Label htmlFor="partnerHoroscopeMatch">Horoscope Match</Label>
                        {editingSection === 'partner' ? (
                          <Input
                          id="partnerHoroscopeMatch"
                            value={partnerExpectations.horoscopeMatch || ''}
                            onChange={(e) => setPartnerExpectations({
                              ...partnerExpectations,
                               horoscopeMatch: e.target.value
                            })}
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {partnerExpectations.horoscopeMatch || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Physical Status */}
                      <div>
                        <Label htmlFor="partnerPhysicalStatus">Physical Status</Label>
                        {editingSection === 'partner' ? (
                          <Select
                            value={partnerExpectations.physicalStatus[0] || ''}
                            onValueChange={(value) => setPartnerExpectations({
                              ...partnerExpectations,
                                physicalStatus: [value]
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Physical Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Normal">Normal</SelectItem>
                              <SelectItem value="Physically Challenged">Physically Challenged</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {partnerExpectations.physicalStatus[0] || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Family Values */}
                      <div>
                        <Label htmlFor="partnerFamilyValues">Family Values</Label>
                        {editingSection === 'partner' ? (
                          <Select
                            value={partnerExpectations.familyValues[0] || ''}
                            onValueChange={(value) => setPartnerExpectations({
                              ...partnerExpectations,
                                familyValues: [value]                      
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Family Values" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Traditional">Traditional</SelectItem>
                              <SelectItem value="Moderate">Moderate</SelectItem>
                              <SelectItem value="Liberal">Liberal</SelectItem>
                              <SelectItem value="Orthodox">Orthodox</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {partnerExpectations.familyValues[0] || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Family Type */}
                      <div>
                        <Label htmlFor="partnerFamilyType">Family Type</Label>
                        {editingSection === 'partner' ? (
                          <Select
                            value={partnerExpectations.familyType[0] || ''}
                            onValueChange={(value) => setPartnerExpectations({
                              ...partnerExpectations,
                                familyType: [value]
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Family Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Nuclear">Nuclear</SelectItem>
                              <SelectItem value="Joint">Joint</SelectItem>
                              <SelectItem value="Extended">Extended</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {partnerExpectations.familyType[0] || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Family Status */}
                      <div>
                        <Label htmlFor="partnerFamilyStatus">Family Status</Label>
                        {editingSection === 'partner' ? (
                          <Select
                            value={partnerExpectations.familyStatus[0] || ''}
                            onValueChange={(value) => setPartnerExpectations({
                              ...partnerExpectations,
                                familyStatus: [value]
                             
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Family Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Middle Class">Middle Class</SelectItem>
                              <SelectItem value="Upper Middle Class">Upper Middle Class</SelectItem>
                              <SelectItem value="Affluent">Affluent</SelectItem>
                              <SelectItem value="Wealthy">Wealthy</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {partnerExpectations.familyStatus[0] || 'Not set'}
                          </p>
                        )}
                      </div>
 
        {/* About Partner - FIXED */}
        <div className="md:col-span-2">
          <Label htmlFor="aboutPartner">About Partner</Label>
          {editingSection === 'partner' ? (
            <Textarea
              id="aboutPartner"
              value={partnerExpectations.aboutPartner}
              onChange={(e) => setPartnerExpectations({
                ...partnerExpectations,
                aboutPartner: e.target.value
              })}
              className="mt-1"
              rows={4}
              placeholder="Describe your ideal partner..."
            />
          ) : (
            <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded min-h-[100px]">
              {partnerExpectations.aboutPartner || 'No description provided'}
            </p>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
)}

              {/* Habits & Lifestyle Section */}
              {activeSection === 'habits' && (
                <Card>
                  <CardHeader className="bg-pink-600 text-white">
                    <div className="flex items-center justify-between">
                      <CardTitle>Habits & Lifestyle</CardTitle>
                      {editingSection === 'habits' ? (
                        <div className="flex space-x-2">
                          <Button
                            onClick={() => handleSaveSection('habits')}
                            disabled={savingSection === 'habits'}
                            className="bg-white text-pink-600 hover:bg-pink-50"
                          >
                            {savingSection === 'habits' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                            Save
                          </Button>
                          <Button
                            onClick={() => handleCancelSection('habits')}
                            variant="outline"
                            className="bg-transparent border-white text-white hover:bg-white/20"
                            disabled={savingSection === 'habits'}
                          >
                            <X className="h-4 w-4 mr-2" />
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => setEditingSection('habits')}
                          className="bg-white text-pink-600 hover:bg-pink-50"
                        >
                          <Edit3 className="h-4 w-4 mr-2" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    {/* Habits & Lifestyle content remains the same */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Body Type */}
                      <div>
                        <Label htmlFor="bodyType">Body Type</Label>
                        {editingSection === 'habits' ? (
                          <Select
                            value={userProfile.personalDetails.bodyType}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, bodyType: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Body Type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Slim">Slim</SelectItem>
                              <SelectItem value="Athletic">Athletic</SelectItem>
                              <SelectItem value="Average">Average</SelectItem>
                              <SelectItem value="Heavy">Heavy</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.bodyType || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Complexion */}
                      <div>
                        <Label htmlFor="complexion">Complexion</Label>
                        {editingSection === 'habits' ? (
                          <Select
                            value={userProfile.personalDetails.complexion}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, complexion: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Complexion" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Very Fair">Very Fair</SelectItem>
                              <SelectItem value="Fair">Fair</SelectItem>
                              <SelectItem value="Wheatish">Wheatish</SelectItem>
                              <SelectItem value="Dark">Dark</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.complexion || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Physical Status */}
                      <div>
                        <Label htmlFor="physicalStatus">Physical Status</Label>
                        {editingSection === 'habits' ? (
                          <Select
                            value={userProfile.personalDetails.physicalStatus}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, physicalStatus: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Physical Status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Normal">Normal</SelectItem>
                              <SelectItem value="Physically Challenged">Physically Challenged</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.physicalStatus || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Blood Group */}
                      <div>
                        <Label htmlFor="bloodGroup">Blood Group</Label>
                        {editingSection === 'habits' ? (
                          <Select
                            value={userProfile.personalDetails.bloodGroup}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, bloodGroup: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Blood Group" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="A+">A+</SelectItem>
                              <SelectItem value="A-">A-</SelectItem>
                              <SelectItem value="B+">B+</SelectItem>
                              <SelectItem value="B-">B-</SelectItem>
                              <SelectItem value="O+">O+</SelectItem>
                              <SelectItem value="O-">O-</SelectItem>
                              <SelectItem value="AB+">AB+</SelectItem>
                              <SelectItem value="AB-">AB-</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.bloodGroup || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Diet */}
                      <div>
                        <Label htmlFor="diet">Diet</Label>
                        {editingSection === 'habits' ? (
                          <Select
                            value={userProfile.personalDetails.diet}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, diet: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Diet" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Vegetarian">Vegetarian</SelectItem>
                              <SelectItem value="Non-Vegetarian">Non-Vegetarian</SelectItem>
                              <SelectItem value="Eggetarian">Eggetarian</SelectItem>
                              <SelectItem value="Vegan">Vegan</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.diet || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Smoking */}
                      <div>
                        <Label htmlFor="smoke">Smoking</Label>
                        {editingSection === 'habits' ? (
                          <Select
                            value={userProfile.personalDetails.smoke}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, smoke: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Smoking Habit" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="No">No</SelectItem>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="Occasionally">Occasionally</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.smoke || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Drinking */}
                      <div>
                        <Label htmlFor="drink">Drinking</Label>
                        {editingSection === 'habits' ? (
                          <Select
                            value={userProfile.personalDetails.drink}
                            onValueChange={(value) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, drink: value }
                            })}
                          >
                            <SelectTrigger className="mt-1">
                              <SelectValue placeholder="Select Drinking Habit" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="No">No</SelectItem>
                              <SelectItem value="Yes">Yes</SelectItem>
                              <SelectItem value="Occasionally">Occasionally</SelectItem>
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded">
                            {userProfile.personalDetails.drink || 'Not set'}
                          </p>
                        )}
                      </div>

                      {/* Hobbies */}
                      <div className="md:col-span-2">
                        <Label htmlFor="hobbies">Hobbies</Label>
                        {editingSection === 'habits' ? (
                          <Textarea
                            id="hobbies"
                            value={userProfile.personalDetails.hobbies}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, hobbies: e.target.value }
                            })}
                            className="mt-1"
                            rows={3}
                            placeholder="List your hobbies..."
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded min-h-[80px]">
                            {userProfile.personalDetails.hobbies || 'No hobbies listed'}
                          </p>
                        )}
                      </div>

                      {/* Interests */}
                      <div className="md:col-span-2">
                        <Label htmlFor="interests">Interests</Label>
                        {editingSection === 'habits' ? (
                          <Textarea
                            id="interests"
                            value={userProfile.personalDetails.interests}
                            onChange={(e) => setUserProfile({
                              ...userProfile,
                              personalDetails: { ...userProfile.personalDetails, interests: e.target.value }
                            })}
                            className="mt-1"
                            rows={3}
                            placeholder="List your interests..."
                          />
                        ) : (
                          <p className="mt-1 text-gray-900 p-2 bg-gray-50 rounded min-h-[80px]">
                            {userProfile.personalDetails.interests || 'No interests listed'}
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Change Password Section */}
              {activeSection === 'password' && (
                <Card>
                  <CardHeader className="bg-red-600 text-white">
                    <div className="flex items-center justify-between">
                      <CardTitle>Change Password</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="max-w-md mx-auto space-y-6">
                      <div>
                        <Label htmlFor="currentPassword">Current Password</Label>
                        <Input
                          id="currentPassword"
                          type="password"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({
                            ...passwordData,
                            currentPassword: e.target.value
                          })}
                          className="mt-1"
                          placeholder="Enter your current password"
                        />
                      </div>

                      <div>
                        <Label htmlFor="newPassword">New Password</Label>
                        <Input
                          id="newPassword"
                          type="password"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({
                            ...passwordData,
                            newPassword: e.target.value
                          })}
                          className="mt-1"
                          placeholder="Enter new password"
                        />
                        <p className="text-sm text-gray-500 mt-1">
                          Password must be at least 8 characters long
                        </p>
                      </div>

                      <div>
                        <Label htmlFor="confirmPassword">Confirm New Password</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({
                            ...passwordData,
                            confirmPassword: e.target.value
                          })}
                          className="mt-1"
                          placeholder="Confirm new password"
                        />
                      </div>

                      <div className="flex space-x-4 pt-4">
                        <Button
                          onClick={async () => {
                            if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
                              alert('Please fill all password fields');
                              return;
                            }

                            if (passwordData.newPassword !== passwordData.confirmPassword) {
                              alert('New password and confirm password do not match');
                              return;
                            }

                            if (passwordData.newPassword.length < 8) {
                              alert('New password must be at least 8 characters long');
                              return;
                            }

                            setSavingSection('password');
                            try {
                              const response = await fetch(`${API_BASE_URL}/users/change-password`, {
                                method: 'POST',
                                headers: {
                                  'Content-Type': 'application/json',
                                },
                                body: JSON.stringify({
                                  userId: userProfile.userId,
                                  currentPassword: passwordData.currentPassword,
                                  newPassword: passwordData.newPassword
                                }),
                              });

                              const result = await response.json();

                              if (result.success) {
                                alert('Password changed successfully!');
                                setPasswordData({
                                  currentPassword: '',
                                  newPassword: '',
                                  confirmPassword: ''
                                });
                              } else {
                                alert(result.message || 'Failed to change password');
                              }
                            } catch (error) {
                              console.error('Password change error:', error);
                              alert('Failed to change password. Please try again.');
                            } finally {
                              setSavingSection(null);
                            }
                          }}
                          disabled={savingSection === 'password'}
                          className="bg-red-600 hover:bg-red-700 text-white flex-1"
                        >
                          {savingSection === 'password' ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Changing Password...
                            </>
                          ) : (
                            'Change Password'
                          )}
                        </Button>

                        <Button
                          onClick={() => setPasswordData({
                            currentPassword: '',
                            newPassword: '',
                            confirmPassword: ''
                          })}
                          variant="outline"
                          className="bg-transparent border-gray-300 flex-1"
                          disabled={savingSection === 'password'}
                        >
                          Clear
                        </Button>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
                        <div className="flex items-start">
                          <Shield className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
                          <div>
                            <h4 className="font-medium text-blue-800">Password Security Tips</h4>
                            <ul className="text-sm text-blue-700 mt-2 space-y-1">
                              <li>• Use at least 8 characters</li>
                              <li>• Include numbers and special characters</li>
                              <li>• Avoid using personal information</li>
                              <li>• Don't reuse old passwords</li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Documents Section */}
              {activeSection === 'documents' && (
                <Card>
                  <CardHeader className="bg-indigo-600 text-white">
                    <div className="flex items-center justify-between">
                      <CardTitle>Documents</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-6">
                      {/* Document Upload Section */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">Upload Your Documents</h3>
                        <p className="text-gray-600 mb-6 text-center">
                          Upload your verification documents for profile and income verification
                        </p>
                        
                        {/* Document Selection Dropdown */}
                        <div className="max-w-md mx-auto space-y-4">
                          <div>
                            <Label htmlFor="documentType" className="text-sm font-medium text-gray-700">
                              Select Document Type
                            </Label>
                            <Select onValueChange={(value) => {
                              setSelectedDocumentType(value);
                            }} value={selectedDocumentType}>
                              <SelectTrigger className="mt-1">
                                <SelectValue placeholder="Choose document type..." />
                              </SelectTrigger>
                              <SelectContent>
                                {/* Profile Verification Documents */}
                                <SelectItem value="Aadhaar Front">Aadhaar Card - Front</SelectItem>
                                <SelectItem value="Aadhaar Back">Aadhaar Card - Back</SelectItem>
                                <SelectItem value="Pan Card">PAN Card</SelectItem>
                                <SelectItem value="Passport Photo">Passport Size Photo</SelectItem>
                                <SelectItem value="Voter Id">Voter ID Card</SelectItem>
                                <SelectItem value="Driving License">Driving License</SelectItem>
                                <SelectItem value="Passport">Passport</SelectItem>
                                
                                {/* Income Verification Documents */}
                                <SelectItem value="Salary Slip">Salary Slip (Latest 3 months)</SelectItem>
                                <SelectItem value="Form 16">Form 16</SelectItem>
                                <SelectItem value="Bank Statement">Bank Statement (6 months)</SelectItem>
                                <SelectItem value="Income Certificate">Income Certificate</SelectItem>
                                <SelectItem value="ITR">Income Tax Return (ITR)</SelectItem>
                                <SelectItem value="Business Proof">Business Proof</SelectItem>
                                
                                {/* Educational Documents */}
                                <SelectItem value="Degree Certificate">Degree Certificate</SelectItem>
                                <SelectItem value="Marksheet">Marksheet</SelectItem>
                                <SelectItem value="Professional Certificate">Professional Certificate</SelectItem>
                                
                                {/* Other Documents */}
                                <SelectItem value="Caste Certificate">Caste Certificate</SelectItem>
                                <SelectItem value="Domicile Certificate">Domicile Certificate</SelectItem>
                                <SelectItem value="Birth Certificate">Birth Certificate</SelectItem>
                                <SelectItem value="Marriage Certificate">Marriage Certificate</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          {/* File Upload Input */}
                          <div>
                            <Label htmlFor="documentFile" className="text-sm font-medium text-gray-700">
                              Upload Document
                            </Label>
                            <Input
                              id="documentFile"
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                              className="mt-1 border-2 border-blue-400 bg-blue-50 hover:border-blue-600 hover:bg-blue-100 cursor-pointer transition-all duration-200 rounded-md"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                setSelectedFile(file || null);
                                if (file) {
                                  console.log('Selected file:', file.name, file.type, file.size);
                                }
                              }}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Supported formats: PDF, JPG, JPEG, PNG, DOC, DOCX (Max: 10MB)
                            </p>
                          </div>

                          <Button
                            className="w-full bg-indigo-600 hover:bg-indigo-700"
                            onClick={handleDocumentUpload}
                            disabled={uploadingDocument || !selectedDocumentType || !selectedFile}
                          >
                            {uploadingDocument ? (
                              <>
                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="h-4 w-4 mr-2" />
                                Upload Document
                              </>
                            )}
                          </Button>
                        </div>
                      </div>

                      {/* Uploaded Documents List */}
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Uploaded Documents ({userDocuments.length})</h3>
                        
                        {userDocuments.length > 0 ? (
                          <div className="space-y-4">
                            {userDocuments.map((doc) => (
                              <div key={doc.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                                <div className="flex items-center space-x-3">
                                  <FileText className="h-8 w-8 text-blue-500" />
                                  <div>
                                    <p className="font-medium text-gray-900">{doc.documentType}</p>
                                    <p className="text-sm text-gray-500">{doc.documentFileName} • {doc.documentSizeFormatted}</p>
                                    <Badge variant="outline" className="bg-yellow-100 text-yellow-800 text-xs">
                                      {doc.documentFormat.toUpperCase()}
                                    </Badge>
                                  </div>
                                </div>
                                <div className="flex space-x-2">
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="bg-transparent border-blue-300 text-blue-700 hover:bg-blue-50"
                                    onClick={() => {
                                      // Open document in new tab
                                      window.open(`${API_HOST}${doc.documentUrl}`, '_blank');
                                    }}
                                  >
                                    <Eye className="h-4 w-4 mr-1" />
                                    View
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="bg-transparent border-red-300 text-red-700 hover:bg-red-50"
                                    onClick={async () => {
                                      if (window.confirm('Are you sure you want to delete this document?')) {
                                        try {
                                          const response = await fetch(`${API_BASE_URL}/user-documents/${doc.id}`, {
                                            method: 'DELETE'
                                          });
                                          
                                          const result = await response.json();
                                          if (result.success) {
                                            alert('Document deleted successfully!');
                                            fetchUserDocuments();
                                          } else {
                                            alert('Failed to delete document');
                                          }
                                        } catch (error) {
                                          console.error('Delete error:', error);
                                          alert('Error deleting document');
                                        }
                                      }
                                    }}
                                  >
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                            <p>No documents uploaded yet</p>
                            <p className="text-sm">Upload your first document to get started</p>
                          </div>
                        )}
                      </div>

                      {/* Document Requirements */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <Shield className="h-6 w-6 text-blue-600 mr-3" />
                          <h3 className="text-lg font-semibold text-blue-800">Required Documents for Verification</h3>
                        </div>
                        
                        {/* Profile Verification Documents */}
                        <div className="mb-4">
                          <h4 className="font-medium text-blue-800 mb-2">Profile Verification (Mandatory)</h4>
                          <ul className="space-y-1 text-sm text-blue-700">
                            <li className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              Aadhaar Card (Front & Back)
                            </li>
                            <li className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              PAN Card
                            </li>
                            <li className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              Passport Size Photo
                            </li>
                            <li className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              Any Government ID (Voter ID/Driving License/Passport)
                            </li>
                          </ul>
                        </div>

                        {/* Income Verification Documents */}
                        <div>
                          <h4 className="font-medium text-blue-800 mb-2">Income Verification (Choose One)</h4>
                          <ul className="space-y-1 text-sm text-blue-700">
                            <li className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              Salary Slip (Latest 3 months)
                            </li>
                            <li className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              Form 16
                            </li>
                            <li className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              Bank Statement (6 months)
                            </li>
                            <li className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              Income Tax Return (ITR)
                            </li>
                            <li className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              Business Proof (for Business Owners)
                            </li>
                          </ul>
                        </div>

                        <p className="text-sm text-blue-700 mt-4">
                          All documents are securely stored and encrypted. Verification usually takes 24-48 hours.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Profile Photos Section - Redesigned like Documents */}
              {activeSection === 'photos' && (
                <Card>
                  <CardHeader className="bg-red-600 text-white">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle>Profile Photos</CardTitle>
                      <div className="mt-2">
                        <fieldset className="flex items-center gap-4">
                          <legend className="sr-only">Profile photos view mode</legend>

                          <label className="inline-flex items-center text-sm select-none">
                            <input
                              type="radio"
                              name="profilePhotosView"
                              value="Public"
                              checked={photoViewMode === 'Public'}
                              onChange={() => {
                                setPhotoViewMode('Public');
                                persistPhotoViewMode('Public');
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                              aria-label="Public View"
                            />
                            <span className="ml-2">Public View</span>
                          </label>

                          <label className="inline-flex items-center text-sm select-none">
                            <input
                              type="radio"
                              name="profilePhotosView"
                              value="Request"
                              checked={photoViewMode === 'Request'}
                              onChange={() => {
                                setPhotoViewMode('Request');
                                persistPhotoViewMode('Request');
                              }}
                              className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                              aria-label="Request View"
                            />
                            <span className="ml-2">Request View</span>
                          </label>
                        </fieldset>
                      </div>
                        <CardDescription className="text-red-100">
                        </CardDescription>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className="bg-white text-red-600">
                          {userPhotos.length}/10 Photos
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-6">
                      {/* Photo Upload Area */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
                        <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2 text-center">Upload Your Photos</h3>
                        <p className="text-gray-600 mb-6 text-center">
                          Upload your profile photos for verification and better matches
                        </p>
                        
                        {/* File Upload Input */}
                        <div className="max-w-md mx-auto space-y-4">
                          <div>
                            <Label htmlFor="photoFile" className="text-sm font-medium text-gray-700">
                              Select Photos
                            </Label>
                            <Input
                              id="photoFile"
                              type="file"
                              accept=".jpg,.jpeg,.png,.gif,.bmp,.webp"
                              multiple
                              className="mt-1 border-2 border-red-400 bg-red-50 hover:border-red-600 hover:bg-red-100 cursor-pointer transition-all duration-200 rounded-md"
                              onChange={async (e) => {
                                const files = e.target.files;
                                if (!files || !userProfile) return;

                                // Check photo limit
                                if (userPhotos.length + files.length > 10) {
                                  alert(`You can only upload ${10 - userPhotos.length} more photos. Maximum limit is 10 photos.`);
                                  return;
                                }

                                setUploadingPhoto(true);

                                try {
                                  // Upload each photo using the dedicated photo upload endpoint
                                  for (const file of Array.from(files)) {
                                    const formData = new FormData();
                                    formData.append('userId', userProfile.userId);
                                    formData.append('user_id', userProfile.userId);
                                    // Include selected photo view mode so backend (photo-upload-sql.js) can store it in DB
                                    formData.append('viewStatus', photoViewMode); // 'Public' or 'Request'
                                    formData.append('file', file);
                                    
                                    console.log('📤 Uploading photo to dedicated endpoint:', {
                                      userId: userProfile.userId,
                                      fileName: file.name,
                                      fileSize: file.size,
                                      fileType: file.type,
                                      viewStatus: photoViewMode // ✅ Log to verify
                                    });

                                    const response = await fetch(`${API_BASE_URL}/photo-upload/upload`, {
                                      method: 'POST',
                                      body: formData,
                                    });

                                    const result = await response.json();

                                    if (result.success) {
                                      console.log('✅ Photo uploaded successfully:', result);
                                      
                                      // Add the new photo to local state immediately
                                      const newPhoto = {
                                        id: result.photoId,
                                        src: API_CONFIG.getMediaUrl(result.fileInfo.fileUrl),
                                        fileName: result.fileInfo.fileName,
                                        fileSize: result.fileInfo.fileSize,
                                        uploadDate: new Date().toISOString(),
                                        isProfileDP: false,
                                        viewStatus: result.fileInfo?.viewStatus || photoViewMode // ✅ Get viewStatus from response
                                      };
                                      
                                      setUserPhotos(prev => [...prev, newPhoto]);
                                    } else {
                                      throw new Error(result.message || 'Photo upload failed');
                                    }
                                  }

                                  alert('Photos uploaded successfully!');

                                } catch (error) {
                                  console.error('❌ Photo upload error:', error);
                                  alert(`Failed to upload photos: ${error.message}`);
                                } finally {
                                  setUploadingPhoto(false);
                                  // Clear file input
                                  e.target.value = '';
                                }
                              }}
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              Supported formats: JPG, JPEG, PNG, GIF, BMP, WEBP (Max: 5MB per photo)
                            </p>
                          </div>

                          <div className="text-sm text-gray-500 space-y-1">
                            <p>• Maximum 10 photos allowed per user</p>
                            <p>• Select one photo as your Profile Display Picture (DP)</p>
                            <p>• Photo View choose as for Public View or On Request View\</p>
                          </div>
                        </div>
                      </div>

                      {/* Uploaded Photos List */}
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Uploaded Photos ({userPhotos.length})</h3>
                        
                        {userPhotos.length > 0 ? (
                          <div className="space-y-4">
                            {userPhotos.map((photo, index) => (
                              <div key={photo.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                                <div className="flex items-center space-x-4">
                                  {/* Photo Thumbnail */}
                                  <div className="w-16 h-16 bg-gray-100 rounded-lg overflow-hidden border border-gray-300">
                                    <img 
                                      src={photo.src} 
                                      alt={`Profile photo ${index + 1}`}
                                      className="w-full h-full object-cover"
                                      onError={(e) => {
                                        console.log('❌ Image failed to load:', photo.src);
                                        e.currentTarget.src = 'https://via.placeholder.com/64x64?text=Photo';
                                      }}
                                    />
                                  </div>
                                  
                                  {/* Photo Info */}
                                  <div>
                                    <p className="font-medium text-gray-900">Photo {index + 1}</p>
                                    <p className="text-sm text-gray-500">
                                      {photo.fileName} • {formatFileSize(photo.fileSize)}
                                    </p>
                                    <div className="flex items-center space-x-2 mt-1">
                                      <Badge variant="outline" className="bg-gray-100 text-gray-800 text-xs">
                                        {photo.fileName.split('.').pop()?.toUpperCase()}
                                      </Badge>
                                      {photo.isProfileDP && (
                                        <Badge className="bg-green-600 text-white text-xs">
                                          <CheckCircle className="h-3 w-3 mr-1" />
                                          Profile DP
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Action Buttons */}
                                <div className="flex items-center space-x-2">
                                  {/* Set as DP Radio */}
                                  <div className="flex items-center space-x-2 mr-4">
                                    <input
                                      type="radio"
                                      id={`set-dp-${photo.id}`}
                                      name="profileDP"
                                      checked={photo.isProfileDP}
                                      onChange={() => handleSetProfileDP(photo.id)}
                                      disabled={settingDP === photo.id}
                                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
                                    />
                                    <Label 
                                      htmlFor={`set-dp-${photo.id}`}
                                      className="text-sm text-gray-700 cursor-pointer"
                                    >
                                      {settingDP === photo.id ? 'Setting...' : 'Set as DP'}
                                    </Label>
                                  </div>
                                  
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="bg-transparent border-blue-300 text-blue-700 hover:bg-blue-50"
                                    onClick={() => {
                                      // Open photo in new tab
                                      window.open(photo.src, '_blank');
                                    }}
                                  >
                                    <Eye className="h-4 w-4 mr-1" />
                                    View
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="bg-transparent border-red-300 text-red-700 hover:bg-red-50"
                                    onClick={() => handleDeletePhoto(photo.id)}
                                    disabled={deletingPhoto === photo.id}
                                  >
                                    {deletingPhoto === photo.id ? (
                                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                                    ) : (
                                      <Trash2 className="h-4 w-4 mr-1" />
                                    )}
                                    Delete
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 text-gray-500">
                            <Camera className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                            <p>No photos uploaded yet</p>
                            <p className="text-sm">Upload your first photo to get started</p>
                          </div>
                        )}

                        {/* Photo Limit Warning */}
                        {userPhotos.length >= 10 && (
                          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-center">
                              <AlertCircle className="h-5 w-5 text-yellow-600 mr-3" />
                              <p className="text-yellow-800 text-sm">
                                You have reached the maximum limit of 10 photos. Delete some photos to upload new ones.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Photo Requirements */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <Camera className="h-6 w-6 text-blue-600 mr-3" />
                          <h3 className="text-lg font-semibold text-blue-800">Photo Requirements</h3>
                        </div>
                        
                        {/* Profile Verification Photos */}
                        <div className="mb-4">
                          <h4 className="font-medium text-blue-800 mb-2">Recommended Photos</h4>
                          <ul className="space-y-1 text-sm text-blue-700">
                            <li className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              Clear, high-quality headshots
                            </li>
                            <li className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              Smiling, friendly expressions
                            </li>
                            <li className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              Well-lit, professional photos
                            </li>
                            <li className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              Recent photos (last 2 years)
                            </li>
                            <li className="flex items-center">
                              <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                              Solo photos without other people
                            </li>
                          </ul>
                        </div>

                        {/* Avoid These Photos */}
                        <div>
                          <h4 className="font-medium text-blue-800 mb-2">Avoid These Photos</h4>
                          <ul className="space-y-1 text-sm text-blue-700">
                            <li className="flex items-center">
                              <X className="h-4 w-4 text-red-500 mr-2" />
                              Blurry or low-quality images
                            </li>
                            <li className="flex items-center">
                              <X className="h-4 w-4 text-red-500 mr-2" />
                              Group photos with others
                            </li>
                            <li className="flex items-center">
                              <X className="h-4 w-4 text-red-500 mr-2" />
                              Sunglasses or hats covering face
                            </li>
                            <li className="flex items-center">
                              <X className="h-4 w-4 text-red-500 mr-2" />
                              Inappropriate or offensive content
                            </li>
                            <li className="flex items-center">
                              <X className="h-4 w-4 text-red-500 mr-2" />
                              Watermarked or edited photos
                            </li>
                          </ul>
                        </div>

                        <p className="text-sm text-blue-700 mt-4">
                          Your photos will be reviewed by our team. Verification usually takes 24-48 hours.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Profile Videos Section */}
              {activeSection === 'videos' && (
                <Card>
                  <CardHeader className="bg-purple-600 text-white">
                    <div className="flex items-center justify-between">
                      <CardTitle>Profile Videos</CardTitle>
                      <div className="flex items-center space-x-2">
                        <Badge variant="outline" className="bg-white text-purple-600">
                          {userProfile?.videos?.length || 0}/4 Videos
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-6">
                      {/* Hidden File Input */}
                      <Input
                        id="videoUpload"
                        type="file"
                        accept=".mp4,.avi,.mov,.wmv,.flv,.webm,.mkv,.m4v"
                        multiple
                        className="hidden"
                        onChange={async (e) => {
                          const files = e.target.files;
                          if (!files || !userProfile) return;

                          const remaining = 4 - (userProfile?.videos?.length || 0);
                          const toUpload = Array.from(files).slice(0, Math.max(0, remaining));

                          if (toUpload.length === 0) {
                            alert('You have reached the maximum limit of 4 videos.');
                            e.currentTarget.value = '';
                            return;
                          }

                          try {
                            for (const file of toUpload) {
                              // Get duration in seconds
                              const durationSec = await getVideoDurationSeconds(file);

                              const formData = new FormData();
                              formData.append('userId', userProfile.userId);
                              formData.append('duration', String(durationSec));
                              formData.append('file', file);

                              const res = await fetch(`${API_BASE_URL}/video-upload/upload`, {
                                method: 'POST',
                                body: formData
                              });

                              const result = await res.json();
                              if (!result.success) {
                                throw new Error(result.message || 'Video upload failed');
                              }

                              // Push to local state
                              const newVid = {
                                id: String(result.videoId || Date.now()),
                                title: '',
                                src: API_CONFIG.getMediaUrl(result.fileInfo.fileUrl),
                                duration: result.fileInfo.duration || '',
                                status: 'pending' as const,
                                uploadedAt: new Date().toISOString(),
                                fileName: result.fileInfo.fileName
                              };

                              setUserProfile(prev => prev ? ({ ...prev, videos: [...(prev.videos || []), newVid] }) : prev);
                            }

                            alert('Video(s) uploaded successfully!');
                          } catch (error: any) {
                            console.error('❌ Video upload error:', error);
                            alert(error.message || 'Failed to upload video(s). Please try again.');
                          } finally {
                            // Clear input
                            e.currentTarget.value = '';
                          }
                        }}
                      />

                      {/* Video Upload Area */}
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                        <Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Your Videos</h3>
                        <p className="text-gray-600 mb-4">
                          Upload up to 4 introduction videos in MP4, AVI, MOV, WMV, FLV, WEBM, MKV, or M4V format
                        </p>
                        <div className="space-y-2 max-w-md mx-auto text-sm text-gray-500">
                          <p>• Maximum 4 videos allowed</p>
                          <p>• Supported formats: MP4, AVI, MOV, WMV, FLV, WEBM, MKV, M4V</p>
                          <p>• Maximum file size: 50MB per video</p>
                          <p>• Recommended length: 1-3 minutes</p>
                          <p>• Clear audio and good lighting recommended</p>
                        </div>
                        <Button 
                          className="mt-6 bg-purple-600 hover:bg-purple-700"
                          onClick={() => document.getElementById('videoUpload')?.click()}
                        >
                          <Upload className="h-4 w-4 mr-2" />
                          Select Videos
                        </Button>
                      </div>

                      {/* Uploaded Videos Grid */}
                      <div className="bg-white border border-gray-200 rounded-lg p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Videos</h3>
                        
                        {userProfile?.videos && userProfile.videos.length > 0 ? (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {userProfile.videos.map((video, index) => (
                              <div key={video.id} className="relative group">
                                {/* Video Container */}
                                <div className="bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                                  {/* Video Thumbnail/Player */}
                                  <div className="aspect-video bg-black relative">
                                    <div className="w-full h-full flex items-center justify-center">
                                      <Video className="h-16 w-16 text-white opacity-70" />
                                    </div>
                                    
                                    {/* Video Duration Badge */}
                                    <div className="absolute bottom-2 right-2">
                                      <Badge className="bg-black bg-opacity-70 text-white text-xs">
                                        {video.duration}
                                      </Badge>
                                    </div>

                                    {/* Play Button Overlay */}
                                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                                      <Button
                                        size="lg"
                                        className="bg-white text-purple-600 hover:bg-purple-50 rounded-full w-16 h-16"
                                        onClick={() => {
                                          // Open video in modal or player
                                          console.log('Play video:', video.id);
                                          // In production, this would open a video player modal
                                          alert(`Playing video: ${video.title}\n\nIn production, this would open a video player.`);
                                        }}
                                      >
                                        <Play className="h-6 w-6 ml-1" />
                                      </Button>
                                    </div>

                                    {/* Overlay on Hover for Actions */}
                                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                      <div className="flex space-x-1">
                                        <Button
                                          size="sm"
                                          className="bg-white text-gray-900 hover:bg-gray-100"
                                          onClick={() => {
                                            // Open video in modal or new tab
                                            console.log('View video:', video.id);
                                            // In production, this would open a video player
                                            alert(`Viewing video: ${video.title}\n\nIn production, this would open a video player.`);
                                          }}
                                        >
                                          <Eye className="h-4 w-4" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          className="bg-red-600 text-white hover:bg-red-700"
                                          onClick={async () => {
                                            if (!window.confirm('Are you sure you want to delete this video?')) return;
                                            try {
                                              const resp = await fetch(`${API_BASE_URL}/video-upload/${video.id}`, {
                                                method: 'DELETE'
                                              });
                                              const out = await resp.json();
                                              if (out.success) {
                                                setUserProfile(prev => prev
                                                  ? ({ ...prev, videos: (prev.videos || []).filter(v => v.id !== video.id) })
                                                  : prev
                                                );
                                                alert('Video deleted successfully!');
                                                // Refresh videos list
                                                fetchUserVideos();
                                              } else {
                                                alert(out.message || 'Failed to delete video');
                                              }
                                            } catch (err) {
                                              console.error('❌ Delete video failed:', err);
                                              alert('Failed to delete video. Please try again.');
                                            }
                                          }}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Video Info */}
                                  <div className="p-4">
                                    <h4 className="font-medium text-gray-900 text-sm mb-1">{video.title}</h4>
                                    <p className="text-xs text-gray-500">Video #{index + 1}</p>
                                    <div className="mt-2">
                                      <Badge variant="outline" className="bg-yellow-100 text-yellow-800 text-xs">
                                        {video.status === 'pending' ? 'Pending Review' : 
                                         video.status === 'approved' ? 'Approved' : 'Rejected'}
                                      </Badge>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          /* No Videos Message */
                          <div className="text-center py-12">
                            <Video className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                            <p className="text-gray-500 text-lg mb-2">No videos uploaded yet</p>
                            <p className="text-gray-400 text-sm mb-6">
                              Upload your first introduction video to make your profile more engaging
                            </p>
                          </div>
                        )}

                        {/* Video Limit Warning */}
                        {userProfile?.videos && userProfile.videos.length >= 4 && (
                          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-center">
                              <AlertCircle className="h-5 w-5 text-yellow-600 mr-3" />
                              <p className="text-yellow-800 text-sm">
                                You have reached the maximum limit of 4 videos. Delete some videos to upload new ones.
                              </p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Video Guidelines */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                        <div className="flex items-center mb-4">
                          <Video className="h-6 w-6 text-blue-600 mr-3" />
                          <h3 className="text-lg font-semibold text-blue-800">Video Guidelines</h3>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-700">
                          <div className="space-y-2">
                            <h4 className="font-medium text-blue-800">✅ Recommended Videos</h4>
                            <ul className="space-y-1">
                              <li>• Clear, high-quality video (720p or higher)</li>
                              <li>• Good lighting and clear audio</li>
                              <li>• Personal introduction (1-3 minutes)</li>
                              <li>• Smiling, friendly demeanor</li>
                              <li>• Professional background</li>
                              <li>• Talk about your interests and values</li>
                            </ul>
                          </div>
                          <div className="space-y-2">
                            <h4 className="font-medium text-blue-800">❌ Avoid These Videos</h4>
                            <ul className="space-y-1">
                              <li>• Poor audio or video quality</li>
                              <li>• Background noise or distractions</li>
                              <li>• Inappropriate content or language</li>
                              <li>• Videos with other people without consent</li>
                              <li>• Copyrighted music or content</li>
                              <li>• Videos longer than 5 minutes</li>
                            </ul>
                          </div>
                        </div>
                        <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                          <h4 className="font-medium text-purple-800 mb-2">Video Content Ideas</h4>
                          <ul className="text-sm text-purple-700 space-y-1">
                            <li>• Introduce yourself and your background</li>
                            <li>• Share your hobbies and interests</li>
                            <li>• Talk about your career and goals</li>
                            <li>• Share what you're looking for in a partner</li>
                            <li>• Mention your family values and lifestyle</li>
                          </ul>
                        </div>
                        <p className="text-sm text-blue-700 mt-4">
                          Your videos will be reviewed by our team. Verification usually takes 24-48 hours.
                        </p>
                      </div>

                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}